import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Users, FileText, CheckCircle2, XCircle, Clock, ArrowLeft, Filter, ChevronDown,
  Shield, DollarSign, TrendingUp, UserPlus, FileSignature, Building2, Trash2,
  Plus, Edit2, BarChart3, PieChart, Download, FileSpreadsheet, Share2, Copy,
  AlertTriangle, Eye, PenSquare, LineChart, Trophy, Funnel, Linkedin, ExternalLink, Search, Mail, Loader2,
  Activity, MapPin, ArrowUpRight, ArrowDownRight, Network, Briefcase, Target, Phone
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Textarea } from '../components/ui/textarea';
import { Toaster, toast } from 'sonner';
import { motion } from 'framer-motion';
import { DonutChart, GroupedBarChart, FunnelChart } from '../components/Charts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEAL_SOURCE_LABELS = {
  website: 'Website', referral: 'Referral', social_media: 'Social Media',
  job_board: 'Job Board', networking: 'Networking', other: 'Other', unassigned: 'Unassigned'
};
const DEAL_SOURCE_COLORS = {
  website: '#002FA7', referral: '#059669', social_media: '#7C3AED',
  job_board: '#D97706', networking: '#0891B2', other: '#6B7280', unassigned: '#CBD5E1'
};

const ADMIN_TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'agents', label: 'Agents', icon: Users },
  { id: 'compliance', label: 'Compliance', icon: Shield },
  { id: 'recruiter', label: 'Recruiter', icon: Linkedin },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'teams', label: 'Teams', icon: Building2 },
  { id: 'referrals', label: 'Referrals', icon: Share2 },
  { id: 'analytics', label: 'Analytics', icon: LineChart },
  { id: 'blog', label: 'Blog', icon: PenSquare },
  { id: 'reports', label: 'Reports', icon: FileSpreadsheet },
];

function StatCard({ label, value, color = 'text-slate-900', icon: Icon, sub, growth, growthLabel, onClick }) {
  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper onClick={onClick} className={`bg-white dark:bg-slate-800/60 border-l-4 border-l-[#002FA7] border border-slate-200 dark:border-slate-700/60 p-5 rounded-xl hover:shadow-md hover:border-l-[#0040D6] transition-all text-left ${onClick ? 'cursor-pointer active:scale-[0.98]' : ''}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] tracking-[0.2em] uppercase text-slate-400 dark:text-slate-500 font-semibold">{label}</span>
        {Icon && <div className="w-7 h-7 rounded-lg bg-[#002FA7]/8 dark:bg-slate-700/50 flex items-center justify-center"><Icon size={13} className="text-[#002FA7] dark:text-blue-400" /></div>}
      </div>
      <div className={`text-3xl font-['Cabinet_Grotesk'] font-bold tracking-tight ${color}`}>{value}</div>
      <div className="flex items-center justify-between mt-1">
        {sub && <div className="text-[10px] text-slate-400 dark:text-slate-500">{sub}</div>}
        {growth !== undefined && growth !== null && (
          <div className={`flex items-center gap-0.5 text-[10px] font-semibold ${growth >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`}>
            {growth >= 0 ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
            {Math.abs(growth)}%{growthLabel ? ` ${growthLabel}` : ''}
          </div>
        )}
      </div>
    </Wrapper>
  );
}

function ComplianceTab({ api, darkMode }) {
  const [regulations, setRegulations] = useState(null);
  const [licenseData, setLicenseData] = useState(null);
  const [compSubTab, setCompSubTab] = useState('licenses');
  const [sendingNotice, setSendingNotice] = useState(null);
  const [selectedState, setSelectedState] = useState('NY');
  const [verifyLinks, setVerifyLinks] = useState(null);
  const [verificationStatuses, setVerificationStatuses] = useState([]);
  const [verifyingUser, setVerifyingUser] = useState(null);
  const [auditTrail, setAuditTrail] = useState(null);
  const [auditFilter, setAuditFilter] = useState('');

  useEffect(() => {
    axios.get(`${api}/admin/compliance/regulations`, { withCredentials: true }).then(r => setRegulations(r.data)).catch(() => {});
    axios.get(`${api}/admin/compliance/license-tracker`, { withCredentials: true }).then(r => setLicenseData(r.data)).catch(() => {});
  }, [api]);

  useEffect(() => {
    if (compSubTab === 'verify') {
      axios.get(`${api}/admin/compliance/verify-links`, { withCredentials: true }).then(r => setVerifyLinks(r.data)).catch(() => {});
      axios.get(`${api}/admin/compliance/verification-status`, { withCredentials: true }).then(r => setVerificationStatuses(r.data)).catch(() => {});
    }
    if (compSubTab === 'audit') {
      const q = auditFilter ? `?action_type=${auditFilter}` : '';
      axios.get(`${api}/admin/compliance/audit-trail${q}`, { withCredentials: true }).then(r => setAuditTrail(r.data)).catch(() => {});
    }
  }, [api, compSubTab, auditFilter]);

  const sendNotice = async (userId) => {
    setSendingNotice(userId);
    try {
      await axios.post(`${api}/admin/compliance/send-expiry-notice`, { user_id: userId }, { withCredentials: true });
      toast.success('Expiry notice sent to agent and broker');
    } catch { toast.error('Failed to send notice'); }
    setSendingNotice(null);
  };

  const verifyLicense = async (userId, status, notes = '') => {
    setVerifyingUser(userId);
    try {
      await axios.post(`${api}/admin/compliance/verify-license`, { user_id: userId, status, notes }, { withCredentials: true });
      toast.success(`License marked as ${status}`);
      const res = await axios.get(`${api}/admin/compliance/verification-status`, { withCredentials: true });
      setVerificationStatuses(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setVerifyingUser(null);
  };

  const downloadTemplate = async (state, docType) => {
    try {
      const res = await axios.get(`${api}/admin/compliance/templates/${state}/${docType}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `krieger_${state.toLowerCase()}_${docType}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success('Template downloaded!');
    } catch { toast.error('Download failed'); }
  };

  const dk = (light, dark) => darkMode ? dark : light;
  const statusColors = { expired: 'bg-red-100 text-red-700 border-red-200', critical: 'bg-red-50 text-red-600 border-red-200', warning: 'bg-amber-50 text-amber-700 border-amber-200', ok: 'bg-emerald-50 text-emerald-700 border-emerald-200', missing: 'bg-slate-50 text-slate-500 border-slate-200', unknown: 'bg-slate-50 text-slate-400 border-slate-200' };
  const statusLabels = { expired: 'Expired', critical: 'Critical', warning: '< 90 Days', ok: 'Active', missing: 'No Date', unknown: 'Unknown' };
  const verifyColors = { verified: 'bg-emerald-50 text-emerald-700 border-emerald-200', rejected: 'bg-red-50 text-red-700 border-red-200', pending: 'bg-amber-50 text-amber-700 border-amber-200', unverified: 'bg-slate-50 text-slate-500 border-slate-200' };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="admin-compliance-tab">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className={`text-2xl font-['Cabinet_Grotesk'] font-bold ${dk('text-slate-900', 'text-white')}`}>Compliance Center</h1>
          <p className={`text-sm mt-1 ${dk('text-slate-400', 'text-slate-400')}`}>State regulations, license tracking, verification, and audit trail</p>
        </div>
      </div>

      <div className="flex gap-1 mb-6 overflow-x-auto">
        {[
          { id: 'licenses', label: 'License Tracker' },
          { id: 'verify', label: 'Verification' },
          { id: 'templates', label: 'Documents' },
          { id: 'regulations', label: 'State Regulations' },
          { id: 'audit', label: 'Audit Trail' },
        ].map(t => (
          <button key={t.id} onClick={() => setCompSubTab(t.id)} className={`px-4 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${compSubTab === t.id ? 'bg-[#002FA7] text-white' : dk('text-slate-500 bg-slate-100', 'text-slate-400 bg-slate-800')}`} data-testid={`comp-tab-${t.id}`}>{t.label}</button>
        ))}
      </div>

      {/* LICENSE TRACKER */}
      {compSubTab === 'licenses' && licenseData && (
        <div className="space-y-4" data-testid="license-tracker">
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[
              { label: 'Total Agents', val: licenseData.summary.total, color: 'text-slate-900' },
              { label: 'Active', val: licenseData.summary.ok, color: 'text-emerald-600' },
              { label: '< 90 Days', val: licenseData.summary.warning, color: 'text-amber-600' },
              { label: '< 30 Days', val: licenseData.summary.critical, color: 'text-red-600' },
              { label: 'Expired', val: licenseData.summary.expired, color: 'text-red-700' },
            ].map(s => (
              <div key={s.label} className={`border rounded-xl p-4 ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                <div className={`text-[10px] tracking-wider uppercase font-semibold ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{s.label}</div>
                <div className={`text-2xl font-['Cabinet_Grotesk'] font-bold ${s.color}`}>{s.val}</div>
              </div>
            ))}
          </div>

          {/* Agent license table */}
          <div className={`border rounded-xl overflow-hidden ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
            <div className={`px-5 py-3 border-b flex items-center justify-between ${darkMode ? 'border-slate-700' : 'border-slate-100'}`}>
              <h3 className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-slate-800'}`}>All Agent Licenses</h3>
              <span className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Sorted by expiry (soonest first)</span>
            </div>
            <div className="divide-y divide-slate-100 dark:divide-slate-700 max-h-[500px] overflow-y-auto">
              {licenseData.agents.map((agent, i) => (
                <div key={agent.user_id || i} className={`flex items-center gap-4 px-5 py-3 ${darkMode ? 'hover:bg-slate-700/50' : 'hover:bg-slate-50'} transition-colors`} data-testid={`license-row-${i}`}>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-slate-800'}`}>{agent.name}</div>
                    <div className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{agent.email} · {agent.state || 'N/A'} · {agent.license_number || 'No license #'}</div>
                  </div>
                  <div className="text-right shrink-0">
                    {agent.expiry_date ? (
                      <>
                        <div className={`text-xs font-medium ${agent.days_until_expiry !== null && agent.days_until_expiry < 0 ? 'text-red-600' : agent.days_until_expiry <= 30 ? 'text-red-500' : agent.days_until_expiry <= 90 ? 'text-amber-600' : darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                          {agent.expiry_date.slice(0, 10)}
                        </div>
                        <div className="text-[9px] text-slate-400">
                          {agent.days_until_expiry !== null ? (agent.days_until_expiry < 0 ? `${Math.abs(agent.days_until_expiry)}d overdue` : `${agent.days_until_expiry}d left`) : ''}
                        </div>
                      </>
                    ) : (
                      <span className="text-[10px] text-slate-400">No date set</span>
                    )}
                  </div>
                  <Badge className={`text-[8px] shrink-0 border ${statusColors[agent.status]}`}>{statusLabels[agent.status]}</Badge>
                  {(agent.status === 'expired' || agent.status === 'critical' || agent.status === 'warning') && (
                    <Button variant="outline" size="sm" className="text-[9px] h-6 px-2 shrink-0" onClick={() => sendNotice(agent.user_id)} disabled={sendingNotice === agent.user_id} data-testid={`send-notice-${i}`}>
                      {sendingNotice === agent.user_id ? '...' : 'Notify'}
                    </Button>
                  )}
                </div>
              ))}
              {licenseData.agents.length === 0 && (
                <div className="text-center py-10"><p className="text-xs text-slate-400">No agents with license data. Agents set their license info in their profile.</p></div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* STATE REGULATIONS */}
      {compSubTab === 'regulations' && regulations && (
        <div className="space-y-4" data-testid="state-regulations">
          <div className="flex gap-2 mb-2">
            {Object.keys(regulations).map(code => (
              <button key={code} onClick={() => setSelectedState(code)} className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${selectedState === code ? 'bg-[#002FA7] text-white' : darkMode ? 'text-slate-400 bg-slate-800 hover:bg-slate-700' : 'text-slate-500 bg-slate-100 hover:bg-slate-200'}`} data-testid={`state-btn-${code}`}>{regulations[code].state}</button>
            ))}
          </div>

          {regulations[selectedState] && (() => {
            const reg = regulations[selectedState];
            return (
              <div className="space-y-4">
                {/* Header */}
                <div className={`border rounded-xl p-5 ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-[#002FA7]/10 flex items-center justify-center"><Shield size={18} className="text-[#002FA7]" /></div>
                    <div>
                      <h2 className={`text-lg font-['Cabinet_Grotesk'] font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>{reg.state}</h2>
                      <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{reg.agency}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}><div className={`text-[10px] uppercase tracking-wider font-semibold ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Renewal</div><div className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>{reg.renewal_period}</div></div>
                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}><div className={`text-[10px] uppercase tracking-wider font-semibold ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>CE Hours</div><div className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>{reg.ce_hours}h</div></div>
                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}><div className={`text-[10px] uppercase tracking-wider font-semibold ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>License Types</div><div className={`text-[10px] ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{reg.license_types.join(', ')}</div></div>
                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}><div className={`text-[10px] uppercase tracking-wider font-semibold ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Broker License</div><div className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>{reg.broker_license}</div></div>
                  </div>
                </div>

                {/* CE Details */}
                <div className={`border rounded-xl p-5 ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                  <h3 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-white' : 'text-slate-800'}`}>Continuing Education Requirements</h3>
                  <p className={`text-xs leading-relaxed ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>{reg.ce_details}</p>
                </div>

                {/* Escrow */}
                <div className={`border rounded-xl p-5 ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                  <h3 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-white' : 'text-slate-800'}`}>Escrow Account Rules</h3>
                  <p className={`text-xs leading-relaxed ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>{reg.escrow_rules}</p>
                </div>

                {/* Key Statutes */}
                <div className={`border rounded-xl p-5 ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
                  <h3 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-slate-800'}`}>Key Statutes & Regulations</h3>
                  <div className="space-y-2">
                    {reg.key_statutes.map((statute, si) => (
                      <a key={si} href={statute.url} target="_blank" rel="noopener noreferrer" className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${darkMode ? 'border-slate-700 hover:bg-slate-700/50' : 'border-slate-100 hover:bg-slate-50'}`} data-testid={`statute-${selectedState}-${si}`}>
                        <div>
                          <div className={`text-xs font-semibold ${darkMode ? 'text-white' : 'text-slate-800'}`}>{statute.code}</div>
                          <div className={`text-[10px] ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{statute.title}</div>
                        </div>
                        <ExternalLink size={12} className="text-[#002FA7] shrink-0" />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* LICENSE VERIFICATION */}
      {compSubTab === 'verify' && (
        <div className="space-y-4" data-testid="license-verify">
          {/* State board lookup links */}
          {verifyLinks && (
            <div className={`border rounded-xl p-5 ${dk('bg-white border-slate-200', 'bg-slate-800/50 border-slate-700')}`}>
              <h3 className={`text-sm font-semibold mb-3 ${dk('text-slate-800', 'text-white')}`}>State Licensing Board Lookup</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {Object.entries(verifyLinks).map(([code, info]) => (
                  <a key={code} href={info.url} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-3 p-3 border rounded-lg transition-colors ${dk('border-slate-100 hover:bg-slate-50', 'border-slate-700 hover:bg-slate-700/50')}`} data-testid={`verify-link-${code}`}>
                    <div className="w-8 h-8 rounded-lg bg-[#002FA7]/10 flex items-center justify-center shrink-0"><Shield size={14} className="text-[#002FA7]" /></div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-xs font-semibold ${dk('text-slate-800', 'text-white')}`}>{info.name}</div>
                      <div className={`text-[9px] ${dk('text-slate-400', 'text-slate-500')}`}>{info.instructions}</div>
                    </div>
                    <ExternalLink size={12} className="text-[#002FA7] shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Agent verification list */}
          <div className={`border rounded-xl overflow-hidden ${dk('bg-white border-slate-200', 'bg-slate-800/50 border-slate-700')}`}>
            <div className={`px-5 py-3 border-b ${dk('border-slate-100', 'border-slate-700')}`}>
              <h3 className={`text-sm font-semibold ${dk('text-slate-800', 'text-white')}`}>Agent License Verification Status</h3>
            </div>
            <div className="divide-y divide-slate-100 dark:divide-slate-700 max-h-[500px] overflow-y-auto">
              {verificationStatuses.map((agent, i) => (
                <div key={agent.user_id || i} className={`flex items-center gap-4 px-5 py-3 ${dk('hover:bg-slate-50', 'hover:bg-slate-700/50')} transition-colors`} data-testid={`verify-row-${i}`}>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium ${dk('text-slate-800', 'text-white')}`}>{agent.name}</div>
                    <div className={`text-[10px] ${dk('text-slate-400', 'text-slate-500')}`}>{agent.state || 'N/A'} · {agent.license_number || 'No #'}</div>
                  </div>
                  <Badge className={`text-[8px] shrink-0 border ${verifyColors[agent.license_verified]}`}>{agent.license_verified}</Badge>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="outline" size="sm" className="text-[9px] h-6 px-2 text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={() => verifyLicense(agent.user_id, 'verified')} disabled={verifyingUser === agent.user_id} data-testid={`verify-btn-${i}`}>Verify</Button>
                    <Button variant="outline" size="sm" className="text-[9px] h-6 px-2 text-red-500 border-red-200 hover:bg-red-50" onClick={() => verifyLicense(agent.user_id, 'rejected', 'License could not be confirmed')} disabled={verifyingUser === agent.user_id} data-testid={`reject-btn-${i}`}>Flag</Button>
                  </div>
                </div>
              ))}
              {verificationStatuses.length === 0 && <div className="text-center py-8"><p className="text-xs text-slate-400">No agents to verify.</p></div>}
            </div>
          </div>
        </div>
      )}

      {/* DOCUMENT TEMPLATES */}
      {compSubTab === 'templates' && (
        <div className="space-y-4" data-testid="compliance-templates">
          <p className={`text-sm ${dk('text-slate-500', 'text-slate-400')}`}>Download compliance checklists and disclosure forms for each state.</p>
          {['NY', 'PA', 'FL'].map(code => {
            const reg = regulations?.[code];
            if (!reg) return null;
            return (
              <div key={code} className={`border rounded-xl p-5 ${dk('bg-white border-slate-200', 'bg-slate-800/50 border-slate-700')}`} data-testid={`templates-${code}`}>
                <h3 className={`text-sm font-semibold mb-3 ${dk('text-slate-800', 'text-white')}`}>{reg.state}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { id: 'onboarding-checklist', label: 'Onboarding Checklist', desc: 'Pre-onboarding, agreements, compliance training' },
                    { id: 'agency-disclosure', label: 'Agency Disclosure', desc: 'Buyer/seller agent roles and acknowledgement' },
                    { id: 'escrow-checklist', label: 'Escrow Compliance', desc: 'Trust account setup and procedures' },
                    { id: 'renewal-checklist', label: 'Renewal Checklist', desc: 'CE requirements and renewal steps' },
                  ].map(tmpl => (
                    <button key={tmpl.id} onClick={() => downloadTemplate(code, tmpl.id)} className={`text-left p-3 border rounded-lg transition-colors ${dk('border-slate-100 hover:bg-slate-50 hover:border-[#002FA7]/30', 'border-slate-700 hover:bg-slate-700/50')}`} data-testid={`dl-${code}-${tmpl.id}`}>
                      <Download size={14} className="text-[#002FA7] mb-1.5" />
                      <div className={`text-xs font-semibold ${dk('text-slate-800', 'text-white')}`}>{tmpl.label}</div>
                      <div className={`text-[9px] mt-0.5 ${dk('text-slate-400', 'text-slate-500')}`}>{tmpl.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* AUDIT TRAIL */}
      {compSubTab === 'audit' && (
        <div className="space-y-4" data-testid="audit-trail">
          <div className="flex items-center gap-3">
            <select value={auditFilter} onChange={e => setAuditFilter(e.target.value)} className={`text-xs border rounded-lg px-3 py-1.5 ${dk('border-slate-200 bg-white text-slate-600', 'border-slate-700 bg-slate-800 text-slate-300')}`} data-testid="audit-filter">
              <option value="">All Actions</option>
              {(auditTrail?.action_types || []).map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
            </select>
            <span className={`text-[10px] ${dk('text-slate-400', 'text-slate-500')}`}>{auditTrail?.logs?.length || 0} events</span>
          </div>
          <div className={`border rounded-xl overflow-hidden ${dk('bg-white border-slate-200', 'bg-slate-800/50 border-slate-700')}`}>
            <div className="divide-y divide-slate-100 dark:divide-slate-700 max-h-[600px] overflow-y-auto">
              {auditTrail?.logs?.map((log, i) => {
                const timeAgo = log.created_at ? (() => {
                  const diff = Date.now() - new Date(log.created_at).getTime();
                  const mins = Math.floor(diff / 60000);
                  if (mins < 60) return `${mins}m ago`;
                  const hrs = Math.floor(mins / 60);
                  if (hrs < 24) return `${hrs}h ago`;
                  return `${Math.floor(hrs / 24)}d ago`;
                })() : '';
                const typeIcons = { license_verification: '🔍', expiry_notice_sent: '📧', template_download: '📄' };
                return (
                  <div key={log.id || i} className={`flex items-start gap-3 px-5 py-3 ${dk('hover:bg-slate-50', 'hover:bg-slate-700/50')} transition-colors`} data-testid={`audit-row-${i}`}>
                    <span className="text-sm shrink-0 mt-0.5">{typeIcons[log.action_type] || '📋'}</span>
                    <div className="flex-1 min-w-0">
                      <div className={`text-xs ${dk('text-slate-800', 'text-white')}`}>{log.description}</div>
                      <div className={`text-[9px] mt-0.5 ${dk('text-slate-400', 'text-slate-500')}`}>by {log.performed_by_name || log.performed_by} · {timeAgo}</div>
                    </div>
                    <Badge className={`text-[7px] shrink-0 ${dk('bg-slate-50 text-slate-400 border-slate-200', 'bg-slate-700 text-slate-400 border-slate-600')}`}>{log.action_type?.replace(/_/g, ' ')}</Badge>
                  </div>
                );
              })}
              {(!auditTrail?.logs || auditTrail.logs.length === 0) && (
                <div className="text-center py-10"><p className="text-xs text-slate-400">No audit events yet. Actions like license verification, notices sent, and template downloads are logged automatically.</p></div>
              )}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}

function SmartMatchPanel({ api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchSmartMatch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${api}/admin/recruiter/smart-match`, { withCredentials: true });
      setData(res.data);
    } catch { toast.error('Failed to load smart match data'); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchSmartMatch(); }, [fetchSmartMatch]);

  const priorityStyles = { high: 'bg-red-50 border-red-200 text-red-700', medium: 'bg-amber-50 border-amber-200 text-amber-700', low: 'bg-emerald-50 border-emerald-200 text-emerald-700' };
  const priorityLabels = { high: 'High Priority', medium: 'Medium', low: 'Low' };
  const typeIcons = { state_gap: '📍', specialization_gap: '🏢', depth_gap: '🌳' };
  const typeLabels = { state_gap: 'Market Gap', specialization_gap: 'Specialization Gap', depth_gap: 'Depth Gap' };

  return (
    <div className="space-y-6" data-testid="smart-match-panel">
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 size={24} className="animate-spin text-[#002FA7]" /></div>
      ) : data ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Active Agents" value={data.summary.total_agents} icon={Users} />
            <StatCard label="Total Prospects" value={data.summary.total_prospects} icon={Linkedin} />
            <StatCard label="Markets Covered" value={Object.keys(data.summary.by_state).length} icon={Building2} sub={Object.entries(data.summary.by_state).map(([s, c]) => `${s}: ${c}`).join(' · ')} />
            <StatCard label="Suggestions" value={data.suggestions.length} icon={TrendingUp} color="text-[#002FA7]" />
          </div>

          {/* Suggestions */}
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-slate-800">AI-Powered Recruiting Suggestions</h3>
                <p className="text-[10px] text-slate-400">Based on gaps in your current agent network and downline.</p>
              </div>
              <Button variant="outline" size="sm" className="text-xs h-7" onClick={fetchSmartMatch} data-testid="refresh-smart-match">Refresh</Button>
            </div>
            {data.suggestions.length === 0 ? (
              <div className="text-center py-10">
                <CheckCircle2 size={32} className="text-emerald-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Great coverage! No significant market gaps detected.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {data.suggestions.map((s, i) => (
                  <div key={i} className={`border rounded-xl p-4 ${priorityStyles[s.priority] || 'bg-slate-50 border-slate-200'}`} data-testid={`suggestion-${i}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm">{typeIcons[s.type] || '💡'}</span>
                          <Badge className="text-[8px] bg-white/80 border-current">{typeLabels[s.type] || s.type}</Badge>
                          <Badge className="text-[8px] bg-white/50 border-current">{priorityLabels[s.priority]}</Badge>
                          {s.market && <span className="text-[10px] font-medium">{s.market}</span>}
                        </div>
                        <p className="text-xs text-slate-700 leading-relaxed">{s.message}</p>
                        {s.current_agents !== undefined && (
                          <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-500">
                            <span>{s.current_agents} agent{s.current_agents !== 1 ? 's' : ''}</span>
                            {s.active_prospects !== undefined && <span>{s.active_prospects} active prospect{s.active_prospects !== 1 ? 's' : ''}</span>}
                          </div>
                        )}
                      </div>
                      {s.search_url && (
                        <a href={s.search_url} target="_blank" rel="noopener noreferrer">
                          <Button variant="outline" size="sm" className="text-[10px] h-7 gap-1 shrink-0" data-testid={`search-linkedin-${i}`}>
                            <Linkedin size={10} /> Search LinkedIn
                          </Button>
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Market Distribution */}
          {Object.keys(data.summary.by_state).length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-slate-800 mb-3">Agent Distribution by State</h3>
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(data.summary.by_state).map(([state, count]) => (
                  <div key={state} className="bg-slate-50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-['Cabinet_Grotesk'] font-bold text-[#002FA7]">{count}</div>
                    <div className="text-[10px] text-slate-400">{state}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [teams, setTeams] = useState([]);
  const [referralLeaderboard, setReferralLeaderboard] = useState([]);
  const [adminDocs, setAdminDocs] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [docFilter, setDocFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  // Team creation
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamLeader, setNewTeamLeader] = useState('');
  // Team member addition
  const [addMemberTeamId, setAddMemberTeamId] = useState(null);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberSplit, setNewMemberSplit] = useState(90);
  // Inline split edit
  const [editingSplit, setEditingSplit] = useState(null);
  const [editSplitVal, setEditSplitVal] = useState(90);

  // Analytics state
  const [analyticsOverview, setAnalyticsOverview] = useState(null);
  const [agentLeaderboard, setAgentLeaderboard] = useState([]);
  const [dealTrends, setDealTrends] = useState([]);
  const [dealTypeBreakdown, setDealTypeBreakdown] = useState([]);
  const [recruitmentFunnel, setRecruitmentFunnel] = useState(null);
  const [leaderboardPeriod, setLeaderboardPeriod] = useState('all');

  // Blog state
  const [blogPosts, setBlogPosts] = useState([]);
  const [showBlogEditor, setShowBlogEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [blogForm, setBlogForm] = useState({ title: '', slug: '', content: '', excerpt: '', category: 'market-insights', tags: '', meta_title: '', meta_description: '', featured_image: '', status: 'draft' });

  // Rejection reason state
  const [rejectingAppId, setRejectingAppId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Bulk selection state
  const [selectedApps, setSelectedApps] = useState(new Set());
  const [bulkRejectionReason, setBulkRejectionReason] = useState('');
  const [showBulkReject, setShowBulkReject] = useState(false);

  // Recruiter state
  const [recruiterTemplates, setRecruiterTemplates] = useState([]);
  const [searchLinks, setSearchLinks] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [recruiterStats, setRecruiterStats] = useState(null);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [templateFields, setTemplateFields] = useState({ name: '', market: 'New York', state: 'NY', specialization: 'residential and commercial' });
  const [showProspectForm, setShowProspectForm] = useState(false);
  const [prospectForm, setProspectForm] = useState({ name: '', linkedin_url: '', market: 'New York', specialization: '', notes: '' });
  const [prospectFilter, setProspectFilter] = useState('all');
  const [sendEmail, setSendEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [recruiterAnalytics, setRecruiterAnalytics] = useState(null);
  const [recruiterSubTab, setRecruiterSubTab] = useState('outreach'); // outreach, analytics, social, smart-match
  const [smartMatchData, setSmartMatchData] = useState(null);
  const [smartMatchLoading, setSmartMatchLoading] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [viewingApp, setViewingApp] = useState(null);
  const [appSearch, setAppSearch] = useState('');
  const [filterState, setFilterState] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSpec, setFilterSpec] = useState('');
  const [appNotes, setAppNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [notesLoading, setNotesLoading] = useState(false);

  // Application trends state
  const [appTrends, setAppTrends] = useState([]);
  const [enhancedData, setEnhancedData] = useState(null);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('krieger-admin-dark') === 'true');
  const [seeding, setSeeding] = useState(false);

  // Apply dark class to html element
  useEffect(() => {
    if (darkMode) { document.documentElement.classList.add('dark'); }
    else { document.documentElement.classList.remove('dark'); }
    return () => document.documentElement.classList.remove('dark');
  }, [darkMode]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/onboarding-stats`, { withCredentials: true });
      setStats(res.data);
    } catch (err) {
      if (err.response?.status === 403) { toast.error('Admin access required'); navigate('/dashboard'); }
    }
  }, [navigate]);

  const fetchApplications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/applications-full`, { withCredentials: true });
      let filtered = res.data;
      if (statusFilter !== 'all') filtered = filtered.filter(a => a.status === statusFilter);
      setApplications(filtered);
    } catch { /* admin fetch error */ }
    finally { setLoading(false); }
  }, [statusFilter]);

  const fetchTeams = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/teams`, { withCredentials: true });
      setTeams(res.data);
    } catch { /* admin fetch error */ }
  }, []);

  const fetchReferralLeaderboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/referral-leaderboard`, { withCredentials: true });
      setReferralLeaderboard(res.data);
    } catch { /* admin fetch error */ }
  }, []);

  const fetchAdminDocs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/documents/all`, { withCredentials: true });
      setAdminDocs(res.data);
    } catch { /* admin fetch error */ }
  }, []);

  const fetchCompliance = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/compliance`, { withCredentials: true });
      setCompliance(res.data);
    } catch { /* admin fetch error */ }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const [overview, leaderboard, trends, breakdown, funnel, appTrendsRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/overview`, { withCredentials: true }),
        axios.get(`${API}/admin/analytics/agent-leaderboard`, { params: { period: leaderboardPeriod }, withCredentials: true }),
        axios.get(`${API}/admin/analytics/deal-trends`, { withCredentials: true }),
        axios.get(`${API}/admin/analytics/deal-type-breakdown`, { withCredentials: true }),
        axios.get(`${API}/admin/analytics/recruitment-funnel`, { withCredentials: true }),
        axios.get(`${API}/admin/analytics/application-trends`, { withCredentials: true }),
      ]);
      setAnalyticsOverview(overview.data);
      setAgentLeaderboard(leaderboard.data);
      setDealTrends(trends.data);
      setDealTypeBreakdown(breakdown.data);
      setRecruitmentFunnel(funnel.data);
      setAppTrends(appTrendsRes.data);
    } catch { /* analytics fetch error */ }
  }, [leaderboardPeriod]);

  const fetchBlogPosts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/blog/posts`, { withCredentials: true });
      setBlogPosts(res.data);
    } catch { /* blog fetch error */ }
  }, []);

  const fetchRecruiter = useCallback(async () => {
    try {
      const [t, s, p, st, an] = await Promise.all([
        axios.get(`${API}/admin/recruiter/templates`, { withCredentials: true }),
        axios.get(`${API}/admin/recruiter/search-links`, { withCredentials: true }),
        axios.get(`${API}/admin/recruiter/prospects`, { withCredentials: true }),
        axios.get(`${API}/admin/recruiter/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/recruiter/analytics`, { withCredentials: true }),
      ]);
      setRecruiterTemplates(t.data);
      setSearchLinks(s.data);
      setProspects(p.data);
      setRecruiterStats(st.data);
      setRecruiterAnalytics(an.data);
    } catch { /* recruiter fetch error */ }
  }, []);

  useEffect(() => { fetchStats(); fetchApplications(); fetchTeams(); fetchReferralLeaderboard(); fetchAdminDocs(); fetchCompliance(); fetchAnalytics(); fetchBlogPosts(); fetchRecruiter();
    // Fetch enhanced dashboard data
    axios.get(`${API}/admin/analytics/dashboard-enhanced`, { withCredentials: true }).then(r => setEnhancedData(r.data)).catch(() => {});
  }, [fetchStats, fetchApplications, fetchTeams, fetchReferralLeaderboard, fetchAdminDocs, fetchCompliance, fetchAnalytics, fetchBlogPosts, fetchRecruiter]);

  useEffect(() => { if (activeTab === 'analytics') fetchAnalytics(); }, [activeTab, fetchAnalytics]);

  // Dark mode toggle
  const toggleDarkMode = () => {
    setDarkMode(prev => {
      const next = !prev;
      localStorage.setItem('krieger-admin-dark', String(next));
      return next;
    });
  };

  // Seed demo data
  const seedDemoData = async () => {
    setSeeding(true);
    try {
      await axios.post(`${API}/admin/seed-demo`, {}, { withCredentials: true });
      toast.success('Demo data seeded! Refreshing...');
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to seed data'); }
    setSeeding(false);
  };

  const clearDemoData = async () => {
    try {
      await axios.delete(`${API}/admin/seed-demo`, { withCredentials: true });
      toast.success('Demo data cleared! Refreshing...');
      setTimeout(() => window.location.reload(), 1500);
    } catch { toast.error('Failed to clear data'); }
  };

  // Notes helpers
  const fetchNotes = async (appId) => {
    setNotesLoading(true);
    try {
      const res = await axios.get(`${API}/admin/applications/${appId}/notes`, { withCredentials: true });
      setAppNotes(res.data);
    } catch { setAppNotes([]); }
    setNotesLoading(false);
  };
  const addNote = async (appId) => {
    if (!newNote.trim()) return;
    try {
      const res = await axios.post(`${API}/admin/applications/${appId}/notes`, { text: newNote }, { withCredentials: true });
      setAppNotes(prev => [res.data, ...prev]);
      setNewNote('');
    } catch { toast.error('Failed to add note'); }
  };
  const deleteNote = async (appId, noteId) => {
    try {
      await axios.delete(`${API}/admin/applications/${appId}/notes/${noteId}`, { withCredentials: true });
      setAppNotes(prev => prev.filter(n => n.id !== noteId));
    } catch { toast.error('Failed to delete'); }
  };
  useEffect(() => { if (viewingApp?.id) fetchNotes(viewingApp.id); }, [viewingApp?.id]);

  // Filtered applications
  const filteredApps = applications.filter(app => {
    if (appSearch) {
      const q = appSearch.toLowerCase();
      const match = `${app.first_name} ${app.last_name} ${app.email} ${app.license_number || ''} ${app.preferred_location || ''}`.toLowerCase();
      if (!match.includes(q)) return false;
    }
    if (filterState && app.license_state !== filterState) return false;
    if (filterStatus && app.status !== filterStatus) return false;
    if (filterSpec && (app.specialization || '') !== filterSpec) return false;
    return true;
  });

  // Blog helpers
  const resetBlogForm = () => {
    setBlogForm({ title: '', slug: '', content: '', excerpt: '', category: 'market-insights', tags: '', meta_title: '', meta_description: '', featured_image: '', status: 'draft' });
    setEditingPost(null);
    setShowBlogEditor(false);
  };
  const generateSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  const saveBlogPost = async () => {
    if (!blogForm.title.trim() || !blogForm.slug.trim() || !blogForm.content.trim()) { toast.error('Title, slug, and content are required'); return; }
    const payload = { ...blogForm, tags: blogForm.tags.split(',').map(t => t.trim()).filter(Boolean) };
    try {
      if (editingPost) {
        await axios.put(`${API}/admin/blog/posts/${editingPost}`, payload, { withCredentials: true });
        toast.success('Post updated');
      } else {
        await axios.post(`${API}/admin/blog/posts`, payload, { withCredentials: true });
        toast.success('Post created');
      }
      resetBlogForm();
      fetchBlogPosts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save post'); }
  };
  const deleteBlogPost = async (postId) => {
    try { await axios.delete(`${API}/admin/blog/posts/${postId}`, { withCredentials: true }); toast.success('Post deleted'); fetchBlogPosts(); } catch { toast.error('Failed to delete'); }
  };

  const updateStatus = async (appId, newStatus, reason = null) => {
    try {
      const payload = { status: newStatus };
      if (newStatus === 'rejected' && reason) payload.rejection_reason = reason;
      await axios.patch(`${API}/admin/applications/${appId}/status`, payload, { withCredentials: true });
      toast.success(`Application ${newStatus}`);
      setRejectingAppId(null);
      setRejectionReason('');
      fetchApplications(); fetchStats();
    } catch { toast.error('Failed to update status'); }
  };

  const handleReject = (appId) => {
    if (rejectingAppId === appId) {
      updateStatus(appId, 'rejected', rejectionReason);
    } else {
      setRejectingAppId(appId);
      setRejectionReason('');
    }
  };

  const toggleSelectApp = (appId) => {
    setSelectedApps(prev => {
      const next = new Set(prev);
      if (next.has(appId)) next.delete(appId); else next.add(appId);
      return next;
    });
  };
  const selectAllApps = () => {
    if (selectedApps.size === applications.length) setSelectedApps(new Set());
    else setSelectedApps(new Set(applications.map(a => a.id)));
  };
  const bulkAction = async (action) => {
    if (selectedApps.size === 0) return;
    let success = 0;
    for (const appId of selectedApps) {
      try {
        const payload = { status: action };
        if (action === 'rejected' && bulkRejectionReason) payload.rejection_reason = bulkRejectionReason;
        await axios.patch(`${API}/admin/applications/${appId}/status`, payload, { withCredentials: true });
        success++;
      } catch { /* skip failed */ }
    }
    toast.success(`${success} application(s) ${action}`);
    setSelectedApps(new Set());
    setShowBulkReject(false);
    setBulkRejectionReason('');
    fetchApplications(); fetchStats();
  };

  const updateSplit = async (appId, split) => {
    try {
      await axios.patch(`${API}/admin/applications/${appId}/split`, { commission_split: split }, { withCredentials: true });
      toast.success('Split updated');
      setEditingSplit(null);
      fetchApplications();
    } catch { toast.error('Failed to update split'); }
  };

  const updateDealSource = async (appId, source) => {
    try {
      await axios.patch(`${API}/admin/applications/${appId}/deal-source`, { deal_source: source }, { withCredentials: true });
      toast.success('Deal source updated');
      fetchApplications(); fetchStats();
    } catch { toast.error('Failed to update deal source'); }
  };

  const reviewDocument = async (docId, action, notes = '') => {
    try {
      await axios.patch(`${API}/admin/documents/${docId}/review`, { action, notes }, { withCredentials: true });
      toast.success(`Document ${action}`);
      fetchAdminDocs();
    } catch { toast.error('Failed to review document'); }
  };

  const createTeam = async () => {
    if (!newTeamName.trim() || !newTeamLeader.trim()) return;
    try {
      await axios.post(`${API}/admin/teams`, { name: newTeamName, leader_email: newTeamLeader }, { withCredentials: true });
      toast.success('Team created');
      setNewTeamName(''); setNewTeamLeader(''); setShowTeamForm(false);
      fetchTeams(); fetchStats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create team'); }
  };

  const addTeamMember = async (teamId) => {
    if (!newMemberEmail.trim()) return;
    try {
      await axios.post(`${API}/admin/teams/${teamId}/members`, { agent_email: newMemberEmail, commission_split: newMemberSplit }, { withCredentials: true });
      toast.success('Member added');
      setAddMemberTeamId(null); setNewMemberEmail(''); setNewMemberSplit(90);
      fetchTeams();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add member'); }
  };

  const updateMemberSplit = async (teamId, memberId, split) => {
    try {
      await axios.patch(`${API}/admin/teams/${teamId}/members/${memberId}`, { commission_split: split }, { withCredentials: true });
      toast.success('Split updated');
      setEditingSplit(null);
      fetchTeams();
    } catch { toast.error('Failed'); }
  };

  const removeMember = async (teamId, memberId) => {
    try {
      await axios.delete(`${API}/admin/teams/${teamId}/members/${memberId}`, { withCredentials: true });
      toast.success('Member removed');
      fetchTeams();
    } catch { toast.error('Failed'); }
  };

  const deleteTeam = async (teamId) => {
    try {
      await axios.delete(`${API}/admin/teams/${teamId}`, { withCredentials: true });
      toast.success('Team deleted');
      fetchTeams(); fetchStats();
    } catch { toast.error('Failed'); }
  };

  const statusBadge = (status) => {
    const styles = { submitted: 'bg-slate-50 text-slate-600 border-slate-200', under_review: 'bg-blue-50 text-blue-600 border-blue-200', qualified: 'bg-emerald-50 text-emerald-600 border-emerald-200', not_qualified: 'bg-amber-50 text-amber-600 border-amber-200', approved: 'bg-emerald-50 text-emerald-600 border-emerald-200', rejected: 'bg-red-50 text-red-500 border-red-200', pending: 'bg-amber-50 text-amber-600 border-amber-200' };
    const icons = { submitted: <Clock size={10} className="mr-1" />, under_review: <Eye size={10} className="mr-1" />, qualified: <CheckCircle2 size={10} className="mr-1" />, not_qualified: <AlertTriangle size={10} className="mr-1" />, approved: <CheckCircle2 size={10} className="mr-1" />, rejected: <XCircle size={10} className="mr-1" />, pending: <Clock size={10} className="mr-1" /> };
    const labels = { under_review: 'Under Review', not_qualified: 'Not Qualified' };
    return <Badge variant="outline" className={`text-[10px] ${styles[status] || ''}`}>{icons[status]}{labels[status] || status}</Badge>;
  };

  const icaBadge = (signed) => (
    signed
      ? <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-600 border-emerald-200"><FileSignature size={10} className="mr-1" />Signed</Badge>
      : <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-400 border-slate-200"><FileSignature size={10} className="mr-1" />Unsigned</Badge>
  );

  const totalApps = stats?.total_applications || 0;
  const dealSources = stats?.deal_sources || {};

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-[#080E1A] text-slate-200' : 'bg-[#F1F5F9] text-slate-900'}`} data-testid="admin-dashboard">
      <Toaster position="top-right" />

      {/* Applicant Detail Slide-Over */}
      {viewingApp && (
        <div className="fixed inset-0 z-50" data-testid="applicant-detail-panel">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setViewingApp(null)} />
          <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className={`absolute right-0 top-0 bottom-0 w-full max-w-lg overflow-y-auto shadow-2xl ${darkMode ? 'bg-[#0F1729] border-l border-slate-700' : 'bg-white border-l border-slate-200'}`}>
            {/* Header */}
            <div className={`sticky top-0 z-10 px-6 py-4 border-b flex items-center justify-between ${darkMode ? 'bg-[#0F1729] border-slate-700' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold ${
                  viewingApp.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                  viewingApp.status === 'rejected' ? 'bg-red-100 text-red-600' :
                  'bg-[#002FA7]/10 text-[#002FA7]'
                }`}>{(viewingApp.first_name || '?')[0]}{(viewingApp.last_name || '?')[0]}</div>
                <div>
                  <h2 className={`text-base font-['Cabinet_Grotesk'] font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>{viewingApp.first_name} {viewingApp.last_name}</h2>
                  <div className="flex items-center gap-2">{statusBadge(viewingApp.status)} {icaBadge(viewingApp.ica_signed)}</div>
                </div>
              </div>
              <button onClick={() => setViewingApp(null)} className={`p-1.5 rounded-lg transition-colors ${darkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`} data-testid="close-detail-panel"><X size={18} /></button>
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* Contact Info */}
              <div>
                <h3 className={`text-[10px] uppercase tracking-wider font-semibold mb-3 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Contact Information</h3>
                <div className={`space-y-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  <div className="flex items-center gap-3 text-sm"><Mail size={14} className="text-[#002FA7] shrink-0" /> {viewingApp.email}</div>
                  {viewingApp.phone && <div className="flex items-center gap-3 text-sm"><Phone size={14} className="text-[#002FA7] shrink-0" /> {viewingApp.phone}</div>}
                  <div className="flex items-center gap-3 text-sm"><MapPin size={14} className="text-[#002FA7] shrink-0" /> {viewingApp.preferred_location || 'Not specified'}</div>
                </div>
              </div>

              {/* License & Experience */}
              <div className={`border rounded-xl p-4 ${darkMode ? 'border-slate-700 bg-slate-800/50' : 'border-slate-200 bg-slate-50'}`}>
                <h3 className={`text-[10px] uppercase tracking-wider font-semibold mb-3 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>License & Experience</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div><div className={`text-[9px] uppercase ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>License #</div><div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-slate-800'}`}>{viewingApp.license_number || '—'}</div></div>
                  <div><div className={`text-[9px] uppercase ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>State</div><div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-slate-800'}`}>{viewingApp.license_state || '—'}</div></div>
                  <div><div className={`text-[9px] uppercase ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Experience</div><div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-slate-800'}`}>{viewingApp.experience_years || 0} years</div></div>
                  <div><div className={`text-[9px] uppercase ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Specialization</div><div className={`text-sm font-medium capitalize ${darkMode ? 'text-white' : 'text-slate-800'}`}>{(viewingApp.specialization || '—').replace(/_/g, ' ')}</div></div>
                  <div><div className={`text-[9px] uppercase ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Active License?</div><div className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-slate-800'}`}>{viewingApp.has_active_license === 'yes' ? 'Yes' : viewingApp.has_active_license || '—'}</div></div>
                  <div><div className={`text-[9px] uppercase ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Commission Split</div><div className="text-sm font-bold text-[#002FA7]">{viewingApp.commission_split || 90}%</div></div>
                </div>
              </div>

              {/* Transaction History */}
              <div className={`border rounded-xl p-4 ${darkMode ? 'border-slate-700 bg-slate-800/50' : 'border-slate-200 bg-slate-50'}`}>
                <h3 className={`text-[10px] uppercase tracking-wider font-semibold mb-3 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Transaction History</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div><div className={`text-[9px] uppercase ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Sales (Last Year)</div><div className={`text-lg font-['Cabinet_Grotesk'] font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>{viewingApp.sales_transactions_last_year || 0}</div></div>
                  <div><div className={`text-[9px] uppercase ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Rentals (Last Year)</div><div className={`text-lg font-['Cabinet_Grotesk'] font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>{viewingApp.rental_transactions_last_year || 0}</div></div>
                  <div><div className={`text-[9px] uppercase ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Lead Gen Method</div><div className={`text-sm capitalize ${darkMode ? 'text-white' : 'text-slate-700'}`}>{(viewingApp.lead_generation_method || '—').replace(/_/g, ' ')}</div></div>
                  <div><div className={`text-[9px] uppercase ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Marketing Budget</div><div className={`text-sm ${darkMode ? 'text-white' : 'text-slate-700'}`}>{viewingApp.monthly_marketing_budget || '—'}</div></div>
                </div>
              </div>

              {/* About */}
              {viewingApp.about && (
                <div>
                  <h3 className={`text-[10px] uppercase tracking-wider font-semibold mb-2 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>About / Notes</h3>
                  <p className={`text-sm leading-relaxed ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{viewingApp.about}</p>
                </div>
              )}

              {/* Qualification Flags */}
              {viewingApp.qualification_flags && (
                <div>
                  <h3 className={`text-[10px] uppercase tracking-wider font-semibold mb-2 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Qualification</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {viewingApp.qualification_flags.auto_qualified && <Badge className="text-[9px] bg-amber-50 text-amber-700 border-amber-200">Auto-Qualified</Badge>}
                    {viewingApp.experience_years >= 5 && <Badge className="text-[9px] bg-blue-50 text-blue-700 border-blue-200">{viewingApp.experience_years}+ Years Experience</Badge>}
                    {viewingApp.sales_transactions_last_year >= 10 && <Badge className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">High Volume</Badge>}
                    {viewingApp.has_active_license === 'yes' && <Badge className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">Active License</Badge>}
                  </div>
                </div>
              )}

              {/* Meta */}
              <div className={`border-t pt-4 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <h3 className={`text-[10px] uppercase tracking-wider font-semibold mb-2 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Application Details</h3>
                <div className={`space-y-1 text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  <div>Applied: {viewingApp.created_at ? new Date(viewingApp.created_at).toLocaleString() : '—'}</div>
                  <div>Last Updated: {viewingApp.updated_at ? new Date(viewingApp.updated_at).toLocaleString() : '—'}</div>
                  <div>Deal Source: {DEAL_SOURCE_LABELS[viewingApp.deal_source] || 'Unassigned'}</div>
                  {viewingApp.rejection_reason && <div className="text-red-500">Rejection Reason: {viewingApp.rejection_reason}</div>}
                  {viewingApp.referral_code && <div>Referral Code: {viewingApp.referral_code}</div>}
                  <div className={`text-[9px] ${darkMode ? 'text-slate-600' : 'text-slate-400'}`}>ID: {viewingApp.id}</div>
                </div>
              </div>

              {/* Internal Notes Timeline */}
              <div className={`border-t pt-4 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`} data-testid="notes-timeline">
                <h3 className={`text-[10px] uppercase tracking-wider font-semibold mb-3 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Internal Notes</h3>
                <div className="flex gap-2 mb-3">
                  <Textarea value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Add a note about this applicant..." rows={2} className={`text-xs flex-1 resize-none ${darkMode ? 'bg-slate-800 border-slate-700 text-white placeholder:text-slate-500' : ''}`} data-testid="note-input" />
                  <Button onClick={() => addNote(viewingApp.id)} disabled={!newNote.trim()} size="sm" className="bg-[#002FA7] hover:bg-[#002075] text-white text-xs h-8 self-end" data-testid="add-note-btn">Add</Button>
                </div>
                {notesLoading ? (
                  <div className="text-center py-4"><Loader2 size={16} className="animate-spin text-[#002FA7] mx-auto" /></div>
                ) : appNotes.length === 0 ? (
                  <p className={`text-xs text-center py-3 ${darkMode ? 'text-slate-600' : 'text-slate-400'}`}>No notes yet.</p>
                ) : (
                  <div className="space-y-2 max-h-[250px] overflow-y-auto">
                    {appNotes.map((note, i) => (
                      <div key={note.id || i} className={`relative pl-4 pb-2 border-l-2 ${darkMode ? 'border-slate-700' : 'border-[#002FA7]/20'}`} data-testid={`note-${i}`}>
                        <div className={`absolute left-[-5px] top-1 w-2 h-2 rounded-full ${darkMode ? 'bg-blue-400' : 'bg-[#002FA7]'}`} />
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <p className={`text-xs leading-relaxed ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>{note.text}</p>
                            <div className={`text-[9px] mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                              {note.author_name || note.author_email} · {note.created_at ? new Date(note.created_at).toLocaleString() : ''}
                            </div>
                          </div>
                          <button onClick={() => deleteNote(viewingApp.id, note.id)} className="text-slate-300 hover:text-red-500 transition-colors shrink-0 mt-0.5" data-testid={`delete-note-${i}`}><Trash2 size={10} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Quick Actions */}
              <div className="flex flex-wrap gap-2 pt-2">
                {viewingApp.status !== 'approved' && (
                  <Button onClick={() => { updateStatus(viewingApp.id, 'approved'); setViewingApp(prev => ({...prev, status: 'approved'})); }} className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs h-8 gap-1" data-testid="detail-approve-btn">
                    <CheckCircle2 size={12} /> Approve
                  </Button>
                )}
                {viewingApp.status !== 'rejected' && (
                  <Button onClick={() => { handleReject(viewingApp.id); setViewingApp(null); }} variant="outline" className="border-red-200 text-red-500 hover:bg-red-50 text-xs h-8 gap-1" data-testid="detail-reject-btn">
                    <XCircle size={12} /> Reject
                  </Button>
                )}
                <Button onClick={() => { updateStatus(viewingApp.id, 'under_review'); setViewingApp(prev => ({...prev, status: 'under_review'})); }} variant="outline" className={`text-xs h-8 gap-1 ${darkMode ? 'border-slate-600 text-slate-300' : 'border-slate-200 text-slate-600'}`} data-testid="detail-review-btn">
                  <Eye size={12} /> Under Review
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Header */}
      <header className={`border-b-2 sticky top-0 z-40 transition-colors duration-300 ${darkMode ? 'border-blue-500/30 bg-[#0A1128] shadow-lg shadow-blue-950/20' : 'border-[#002FA7] bg-white shadow-md'}`}>
        <div className="max-w-[1400px] mx-auto px-4 md:px-10 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Mobile hamburger */}
            <button onClick={() => setMobileNavOpen(!mobileNavOpen)} className={`md:hidden p-1.5 rounded-lg transition-colors ${darkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-500 hover:bg-slate-100'}`} data-testid="mobile-nav-toggle">
              {mobileNavOpen ? <X size={18} /> : <ChevronDown size={18} />}
            </button>
            <button onClick={() => navigate('/dashboard')} className={`hidden md:block transition-colors ${darkMode ? 'text-slate-400 hover:text-white' : 'text-slate-400 hover:text-slate-700'}`} data-testid="admin-back-btn"><ArrowLeft size={18} /></button>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md bg-[#002FA7] flex items-center justify-center"><span className="text-white text-[10px] font-black">K</span></div>
              <span className={`font-['Cabinet_Grotesk'] text-base font-bold tracking-[0.12em] hidden sm:block ${darkMode ? 'text-white' : 'text-slate-900'}`}>KRIEGER</span>
            </div>
            <Badge className={`text-[8px] font-bold tracking-wider ${darkMode ? 'bg-blue-500/20 text-blue-300 border-blue-400/40' : 'bg-[#002FA7] text-white border-[#002FA7]'}`}>ADMIN</Badge>
          </div>
          {/* Desktop tabs */}
          <div className="hidden md:flex items-center gap-0.5 overflow-x-auto scrollbar-hide">
            {ADMIN_TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-[#002FA7] text-white font-semibold shadow-md shadow-blue-600/20 ring-1 ring-blue-400/30'
                    : darkMode
                      ? 'text-slate-500 hover:text-white hover:bg-white/5'
                      : 'text-slate-400 hover:text-[#002FA7] hover:bg-blue-50'
                }`}
                data-testid={`admin-tab-${tab.id}`}
              >
                <tab.icon size={13} /> <span>{tab.label}</span>
              </button>
            ))}
            <div className={`flex items-center gap-3 ml-3 pl-3 border-l-2 ${darkMode ? 'border-slate-700' : 'border-[#002FA7]/20'}`}>
              <button onClick={toggleDarkMode} className={`p-1.5 rounded-lg transition-all ${darkMode ? 'bg-slate-700 text-amber-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-400 hover:bg-slate-200'}`} data-testid="dark-mode-toggle" title={darkMode ? 'Light mode' : 'Dark mode'}>
                {darkMode ? <span className="text-xs">☀</span> : <span className="text-xs">🌙</span>}
              </button>
              <span className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{user?.email}</span>
              <button onClick={() => { logout(); navigate('/'); }} className={`text-[10px] transition-colors ${darkMode ? 'text-slate-500 hover:text-red-400' : 'text-slate-400 hover:text-red-500'}`} data-testid="admin-logout">Sign Out</button>
            </div>
          </div>
          {/* Mobile right controls */}
          <div className="flex md:hidden items-center gap-2">
            <button onClick={toggleDarkMode} className={`p-1.5 rounded-lg ${darkMode ? 'bg-slate-700 text-amber-300' : 'bg-slate-100 text-slate-400'}`} data-testid="dark-mode-toggle-mobile">
              {darkMode ? <span className="text-xs">☀</span> : <span className="text-xs">🌙</span>}
            </button>
          </div>
        </div>
        {/* Mobile nav dropdown */}
        {mobileNavOpen && (
          <div className={`md:hidden border-t ${darkMode ? 'border-slate-700 bg-[#0A1128]' : 'border-slate-200 bg-white'}`} data-testid="mobile-nav-menu">
            <div className="max-w-[1400px] mx-auto px-4 py-3 grid grid-cols-3 gap-1.5">
              {ADMIN_TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => { setActiveTab(tab.id); setMobileNavOpen(false); }}
                  className={`flex flex-col items-center gap-1 py-2.5 px-2 rounded-xl text-center transition-all ${
                    activeTab === tab.id
                      ? 'bg-[#002FA7] text-white shadow-md'
                      : darkMode ? 'text-slate-400 hover:bg-white/5' : 'text-slate-500 hover:bg-slate-50'
                  }`}
                  data-testid={`mobile-tab-${tab.id}`}
                >
                  <tab.icon size={16} />
                  <span className="text-[9px] font-medium leading-none">{tab.label}</span>
                </button>
              ))}
            </div>
            <div className={`border-t px-4 py-2.5 flex items-center justify-between ${darkMode ? 'border-slate-700' : 'border-slate-100'}`}>
              <span className={`text-[10px] truncate ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{user?.email}</span>
              <button onClick={() => { logout(); navigate('/'); setMobileNavOpen(false); }} className="text-[10px] text-red-500">Sign Out</button>
            </div>
          </div>
        )}
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-8">

        {/* ═══════════ OVERVIEW TAB ═══════════ */}
        {activeTab === 'overview' && stats && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="admin-overview">
            {/* Header Row */}
            <div className="flex items-end justify-between mb-6">
              <div>
                <h1 className={`text-3xl font-['Cabinet_Grotesk'] font-extrabold tracking-tight ${darkMode ? 'text-white' : 'text-slate-900'}`}>Brokerage Overview</h1>
                <p className={`text-sm mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Real-time metrics across all operations</p>
              </div>
              <div className="flex items-center gap-2">
                {enhancedData?.growth && (
                  <div className={`flex items-center gap-2 border rounded-lg px-3 py-1.5 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                    <Activity size={12} className="text-[#002FA7]" />
                    <span className={`text-[10px] ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{enhancedData.growth.apps_this_week} app{enhancedData.growth.apps_this_week !== 1 ? 's' : ''} this week</span>
                  </div>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className={`text-[10px] h-7 gap-1 ${darkMode ? 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700' : ''}`} data-testid="seed-menu-btn">
                      <Plus size={10} /> Demo Data
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className={`${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                    <DropdownMenuItem onClick={seedDemoData} disabled={seeding} className={`text-xs ${darkMode ? 'text-slate-300 hover:bg-slate-700' : ''}`} data-testid="seed-demo-btn">
                      {seeding ? 'Seeding...' : 'Seed Demo Data'}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={clearDemoData} className={`text-xs text-red-500 ${darkMode ? 'hover:bg-slate-700' : ''}`} data-testid="clear-demo-btn">
                      Clear Demo Data
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Primary KPI Row */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6" data-testid="admin-stats">
              <StatCard label="Total Applications" value={totalApps} icon={FileText} growth={enhancedData?.growth?.app_growth_pct} growthLabel="MoM" onClick={() => { setActiveTab('agents'); setFilterStatus(''); }} />
              <StatCard label="Pending Review" value={stats.pending_applications} color="text-amber-500" icon={Clock} onClick={() => { setActiveTab('agents'); setFilterStatus('submitted'); }} />
              <StatCard label="Approved Agents" value={stats.approved_applications} color="text-emerald-600" icon={CheckCircle2} onClick={() => { setActiveTab('agents'); setFilterStatus('approved'); }} />
              <StatCard label="Active Agents" value={stats.total_agents} color="text-[#002FA7]" icon={UserPlus} growth={enhancedData?.growth?.agent_growth_pct} growthLabel="MoM" onClick={() => { setActiveTab('agents'); setFilterStatus('approved'); }} />
              <StatCard label="ICA Signed" value={stats.total_ica_signed} color="text-emerald-600" icon={FileSignature} sub={`${stats.total_ica_unsigned} unsigned`} />
              <StatCard label="Active Teams" value={stats.total_teams} color="text-[#002FA7]" icon={Building2} sub={`${stats.total_team_members} members`} onClick={() => setActiveTab('teams')} />
            </div>

            {/* Downline & Revenue Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="downline-stats">
              <div className="bg-gradient-to-br from-[#0F1729] to-[#002FA7] text-white rounded-xl p-5" data-testid="downline-stats-card">
                <div className="flex items-center gap-2 mb-3">
                  <Network size={14} className="text-blue-300" />
                  <span className="text-[10px] tracking-[0.2em] uppercase text-blue-200 font-semibold">Downline Network</span>
                </div>
                <div className="text-3xl font-['Cabinet_Grotesk'] font-extrabold tracking-tight">{enhancedData?.downline?.total_relationships || 0}</div>
                <div className="text-[10px] text-blue-200 mt-1">active relationships</div>
              </div>
              <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white rounded-xl p-5" data-testid="overrides-paid-card">
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign size={14} className="text-emerald-200" />
                  <span className="text-[10px] tracking-[0.2em] uppercase text-emerald-200 font-semibold">Overrides Paid</span>
                </div>
                <div className="text-3xl font-['Cabinet_Grotesk'] font-extrabold tracking-tight">${(enhancedData?.downline?.total_overrides_paid || 0).toLocaleString()}</div>
                <div className="text-[10px] text-emerald-200 mt-1">${(enhancedData?.downline?.month_overrides || 0).toLocaleString()} this month</div>
              </div>
              <StatCard label="Campaign Revenue" value={`$${(stats.total_revenue || 0).toLocaleString()}`} color="text-emerald-600" icon={DollarSign} />
              <StatCard label="Recruiting Pipeline" value={enhancedData?.recruiting?.total_prospects || 0} color="text-[#002FA7]" icon={Target} sub={`${enhancedData?.recruiting?.hired_prospects || 0} hired`} />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
              {/* Deal Source Donut */}
              <div className="lg:col-span-5 bg-white dark:bg-slate-800/60 border-2 border-slate-200 dark:border-slate-700 rounded-xl p-6 hover:border-[#002FA7]/30 dark:hover:border-blue-500/30 transition-all" data-testid="deal-source-breakdown">
                <h3 className="text-sm font-semibold text-slate-800 font-['Cabinet_Grotesk'] mb-4">Deal Source Breakdown</h3>
                {Object.keys(dealSources).length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <PieChart size={32} className="text-slate-200 mb-2" />
                    <p className="text-xs text-slate-400">No data yet. Assign deal sources to applications.</p>
                  </div>
                ) : (
                  <div className="flex items-center gap-8">
                    <DonutChart data={dealSources} colors={DEAL_SOURCE_COLORS} size={160} strokeWidth={28} />
                    <div className="flex-1 space-y-1.5">
                      {Object.entries(dealSources).sort((a, b) => b[1] - a[1]).map(([key, val]) => (
                        <div key={key} className="flex items-center gap-2 text-xs">
                          <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: DEAL_SOURCE_COLORS[key] || '#CBD5E1' }} />
                          <span className="text-slate-500 flex-1">{DEAL_SOURCE_LABELS[key] || key}</span>
                          <span className="font-semibold text-slate-700">{val}</span>
                          <span className="text-slate-400 w-10 text-right">({totalApps ? Math.round((val / totalApps) * 100) : 0}%)</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Onboarding Funnel */}
              <div className="lg:col-span-7 bg-white dark:bg-slate-800/60 border-2 border-slate-200 dark:border-slate-700 rounded-xl p-6 hover:border-[#002FA7]/30 dark:hover:border-blue-500/30 transition-all" data-testid="onboarding-pipeline">
                <h3 className="text-sm font-semibold text-slate-800 font-['Cabinet_Grotesk'] mb-4">Onboarding Pipeline</h3>
                <FunnelChart
                  maxValue={Math.max(stats.total_agents || 1, 1)}
                  steps={[
                    { label: 'Profile Done', value: stats.onboarding_pipeline?.profile_complete || 0, color: '#3B82F6' },
                    { label: 'ICA Signed', value: stats.onboarding_pipeline?.ica_signed || 0, color: '#6366F1' },
                    { label: 'Docs Uploaded', value: stats.onboarding_pipeline?.documents_uploaded || 0, color: '#8B5CF6' },
                    { label: 'Orientation', value: stats.onboarding_pipeline?.orientation_complete || 0, color: '#059669' },
                  ]}
                />
              </div>
            </div>

            {/* Monthly Recruitment Trend */}
            {stats.monthly_trend && Object.keys(stats.monthly_trend).length > 0 && (
              <div className="bg-white dark:bg-slate-800/60 border-2 border-slate-200 dark:border-slate-700 rounded-xl p-6 mb-6 hover:border-[#002FA7]/30 dark:hover:border-blue-500/30 transition-all" data-testid="monthly-trend">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-slate-800 font-['Cabinet_Grotesk']">Monthly Recruitment Trend</h3>
                  <div className="flex items-center gap-4 text-[10px]">
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-slate-300" /> Applied</span>
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#002FA7]" /> Approved</span>
                  </div>
                </div>
                <GroupedBarChart
                  height={160}
                  data={Object.entries(stats.monthly_trend).sort().map(([month, d]) => ({
                    label: month.slice(5),
                    total: d.total,
                    approved: d.approved,
                  }))}
                />
              </div>
            )}

            {/* Conversion Funnel */}
            {enhancedData?.recruiting && (
              <div className={`border-2 rounded-xl p-6 mb-6 transition-all ${darkMode ? 'bg-slate-800/60 border-slate-700 hover:border-blue-500/30' : 'bg-white border-slate-200 hover:border-[#002FA7]/30'}`} data-testid="conversion-funnel">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Target size={14} className="text-[#002FA7]" />
                    <h3 className={`text-sm font-semibold font-['Cabinet_Grotesk'] ${darkMode ? 'text-white' : 'text-slate-800'}`}>Prospect-to-Hire Conversion</h3>
                  </div>
                  <span className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Full recruiting pipeline</span>
                </div>
                {(() => {
                  const rs = recruiterStats;
                  if (!rs) return null;
                  const stages = [
                    { label: 'Identified', value: rs.by_status?.identified || 0, color: '#94A3B8' },
                    { label: 'Contacted', value: rs.by_status?.contacted || 0, color: '#3B82F6' },
                    { label: 'Replied', value: rs.by_status?.replied || 0, color: '#8B5CF6' },
                    { label: 'Interviewed', value: rs.by_status?.interviewed || 0, color: '#F59E0B' },
                    { label: 'Applied', value: rs.by_status?.applied || 0, color: '#10B981' },
                    { label: 'Hired', value: rs.by_status?.hired || 0, color: '#002FA7' },
                  ];
                  const maxVal = Math.max(...stages.map(s => s.value), 1);
                  const totalProspects = rs.total || 1;
                  return (
                    <div className="space-y-2">
                      {stages.map((stage, i) => {
                        const pct = (stage.value / maxVal) * 100;
                        const convRate = i > 0 && stages[i - 1].value > 0
                          ? Math.round((stage.value / stages[i - 1].value) * 100) : null;
                        return (
                          <div key={stage.label} className="flex items-center gap-3">
                            <div className={`w-24 text-right text-xs shrink-0 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{stage.label}</div>
                            <div className="flex-1 relative">
                              <div className={`h-7 rounded-lg overflow-hidden ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                <div className="h-full rounded-lg transition-all duration-700 flex items-center justify-end pr-2" style={{ width: `${Math.max(pct, 6)}%`, backgroundColor: stage.color }}>
                                  <span className="text-[10px] font-semibold text-white">{stage.value}</span>
                                </div>
                              </div>
                            </div>
                            <div className="w-14 text-right shrink-0">
                              {convRate !== null && (
                                <span className={`text-[9px] font-medium ${convRate >= 50 ? 'text-emerald-500' : convRate >= 25 ? 'text-amber-500' : 'text-red-400'}`}>{convRate}%</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      <div className={`flex items-center justify-between pt-3 mt-2 border-t ${darkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                        <span className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Overall hire rate</span>
                        <span className="text-sm font-bold text-[#002FA7]">{rs.total > 0 ? Math.round(((rs.by_status?.hired || 0) / rs.total) * 100) : 0}%</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Bottom Grid: State Distribution + Activity Feed + Compliance */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* State Distribution */}
              <div className="lg:col-span-4 bg-white dark:bg-slate-800/60 border-2 border-slate-200 dark:border-slate-700 rounded-xl p-5 hover:border-[#002FA7]/30 dark:hover:border-blue-500/30 transition-all" data-testid="state-distribution">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin size={14} className="text-[#002FA7]" />
                  <h3 className="text-sm font-semibold text-slate-800 font-['Cabinet_Grotesk']">Agent Distribution</h3>
                </div>
                {enhancedData?.state_distribution && Object.keys(enhancedData.state_distribution).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(enhancedData.state_distribution).sort((a, b) => b[1] - a[1]).map(([state, count]) => {
                      const max = Math.max(...Object.values(enhancedData.state_distribution), 1);
                      const pct = (count / max) * 100;
                      const stateNames = { NY: 'New York', PA: 'Pennsylvania', FL: 'Florida' };
                      const stateColors = { NY: '#002FA7', PA: '#7C3AED', FL: '#059669' };
                      return (
                        <div key={state} data-testid={`state-${state}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-slate-700">{stateNames[state] || state}</span>
                            <span className="text-xs font-bold text-slate-900">{count}</span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.max(pct, 6)}%`, backgroundColor: stateColors[state] || '#64748B' }} />
                          </div>
                        </div>
                      );
                    })}
                    {enhancedData.specialization_distribution && Object.keys(enhancedData.specialization_distribution).length > 0 && (
                      <div className="pt-3 mt-3 border-t border-slate-100">
                        <div className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold mb-2">By Specialization</div>
                        <div className="flex flex-wrap gap-1.5">
                          {Object.entries(enhancedData.specialization_distribution).map(([spec, count]) => (
                            <span key={spec} className="text-[10px] bg-slate-50 border border-slate-200 text-slate-600 px-2 py-0.5 rounded-md">{spec}: {count}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <MapPin size={24} className="text-slate-200 mx-auto mb-2" />
                    <p className="text-xs text-slate-400">No state data yet</p>
                  </div>
                )}
              </div>

              {/* Recent Activity Feed */}
              <div className="lg:col-span-4 bg-white dark:bg-slate-800/60 border-2 border-slate-200 dark:border-slate-700 rounded-xl p-5 hover:border-[#002FA7]/30 dark:hover:border-blue-500/30 transition-all" data-testid="recent-activity">
                <div className="flex items-center gap-2 mb-4">
                  <Activity size={14} className="text-[#002FA7]" />
                  <h3 className="text-sm font-semibold text-slate-800 font-['Cabinet_Grotesk']">Recent Activity</h3>
                </div>
                {enhancedData?.recent_activity && enhancedData.recent_activity.length > 0 ? (
                  <div className="space-y-0.5 max-h-[320px] overflow-y-auto">
                    {enhancedData.recent_activity.slice(0, 12).map((evt, i) => {
                      const typeStyles = {
                        application: { icon: FileText, color: 'text-blue-500', bg: 'bg-blue-50' },
                        ica_signed: { icon: FileSignature, color: 'text-emerald-500', bg: 'bg-emerald-50' },
                        deal_closed: { icon: DollarSign, color: 'text-amber-500', bg: 'bg-amber-50' },
                      };
                      const style = typeStyles[evt.type] || typeStyles.application;
                      const Icon = style.icon;
                      const timeAgo = evt.timestamp ? (() => {
                        const diff = Date.now() - new Date(evt.timestamp).getTime();
                        const mins = Math.floor(diff / 60000);
                        if (mins < 60) return `${mins}m ago`;
                        const hrs = Math.floor(mins / 60);
                        if (hrs < 24) return `${hrs}h ago`;
                        return `${Math.floor(hrs / 24)}d ago`;
                      })() : '';
                      return (
                        <div key={i} className="flex items-start gap-2.5 py-2 px-2 rounded-lg hover:bg-slate-50 transition-colors" data-testid={`activity-${i}`}>
                          <div className={`w-6 h-6 rounded-md ${style.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                            <Icon size={11} className={style.color} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-slate-700 leading-snug">{evt.message}</div>
                            <div className="text-[9px] text-slate-400 mt-0.5">{timeAgo}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Activity size={24} className="text-slate-200 mx-auto mb-2" />
                    <p className="text-xs text-slate-400">No recent activity</p>
                  </div>
                )}
              </div>

              {/* Compliance Snapshot */}
              <div className="lg:col-span-4" data-testid="compliance-snapshot">
                {compliance ? (
                  <div className="bg-white dark:bg-slate-800/60 border-2 border-slate-200 dark:border-slate-700 rounded-xl p-5 hover:border-[#002FA7]/30 dark:hover:border-blue-500/30 transition-all">
                    <div className="flex items-center gap-2 mb-4">
                      <Shield size={14} className="text-[#002FA7]" />
                      <h3 className={`text-sm font-semibold font-['Cabinet_Grotesk'] ${darkMode ? 'text-white' : 'text-slate-800'}`}>Compliance Health</h3>
                    </div>
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <Clock size={16} className="text-amber-500 shrink-0" />
                        <div className="flex-1">
                          <div className="text-lg font-['Cabinet_Grotesk'] font-bold text-amber-600">{compliance.pending_review_count}</div>
                          <div className="text-[9px] text-amber-500 uppercase tracking-wider font-semibold">Pending Review</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                        <AlertTriangle size={16} className="text-orange-500 shrink-0" />
                        <div className="flex-1">
                          <div className="text-lg font-['Cabinet_Grotesk'] font-bold text-orange-600">{compliance.expiring_count}</div>
                          <div className="text-[9px] text-orange-500 uppercase tracking-wider font-semibold">Expiring Soon</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <XCircle size={16} className="text-red-500 shrink-0" />
                        <div className="flex-1">
                          <div className="text-lg font-['Cabinet_Grotesk'] font-bold text-red-500">{compliance.expired_count}</div>
                          <div className="text-[9px] text-red-500 uppercase tracking-wider font-semibold">Expired</div>
                        </div>
                      </div>
                    </div>
                    {(compliance.expiring_count > 0 || compliance.expired_count > 0) && (
                      <div className="space-y-1 max-h-[150px] overflow-y-auto">
                        {compliance.expired.slice(0, 3).map((d, i) => (
                          <div key={`exp-${i}`} className="flex items-center gap-2 text-[10px] px-2 py-1 bg-red-50/50 rounded">
                            <XCircle size={9} className="text-red-400 shrink-0" />
                            <span className="text-red-600 font-medium truncate">{d.agent_name}</span>
                            <span className="text-red-300 ml-auto shrink-0">{d.expiration_date ? new Date(d.expiration_date).toLocaleDateString() : ''}</span>
                          </div>
                        ))}
                        {compliance.expiring_soon.slice(0, 3).map((d, i) => (
                          <div key={`soon-${i}`} className="flex items-center gap-2 text-[10px] px-2 py-1 bg-amber-50/50 rounded">
                            <AlertTriangle size={9} className="text-amber-400 shrink-0" />
                            <span className="text-amber-700 font-medium truncate">{d.agent_name}</span>
                            <span className="text-amber-400 ml-auto shrink-0">{d.expiration_date ? new Date(d.expiration_date).toLocaleDateString() : ''}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-white border border-slate-200/80 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <Shield size={14} className="text-[#002FA7]" />
                      <h3 className="text-sm font-semibold text-slate-800 font-['Cabinet_Grotesk']">Compliance Health</h3>
                    </div>
                    <div className="text-center py-8">
                      <Shield size={24} className="text-slate-200 mx-auto mb-2" />
                      <p className="text-xs text-slate-400">Loading compliance data...</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Compliance Dashboard Widget */}
            <div className="mt-6" data-testid="compliance-widget">
              <div className="flex items-center gap-2 mb-4">
                <Shield size={16} className={`${darkMode ? 'text-blue-400' : 'text-[#002FA7]'}`} />
                <h3 className={`text-sm font-semibold font-['Cabinet_Grotesk'] ${darkMode ? 'text-white' : 'text-slate-800'}`}>Compliance Overview</h3>
                <button onClick={() => setActiveTab('compliance')} className="text-[10px] text-[#002FA7] hover:underline ml-auto">View Full →</button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <button onClick={() => setActiveTab('compliance')} className={`border-2 rounded-xl p-4 border-l-4 border-l-emerald-500 text-left cursor-pointer hover:shadow-md active:scale-[0.98] transition-all ${darkMode ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle2 size={14} className="text-emerald-500" />
                    <span className={`text-[10px] uppercase tracking-wider font-semibold ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Verified</span>
                  </div>
                  <div className="text-2xl font-['Cabinet_Grotesk'] font-bold text-emerald-600">
                    {enhancedData ? Math.max(0, (enhancedData.summary?.total_agents || 0) - (compliance?.pending_review_count || 0) - (compliance?.expired_count || 0)) : '—'}
                  </div>
                  <div className={`text-[9px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>licenses in good standing</div>
                </button>
                <button onClick={() => setActiveTab('compliance')} className={`border-2 rounded-xl p-4 border-l-4 border-l-amber-500 text-left cursor-pointer hover:shadow-md active:scale-[0.98] transition-all ${darkMode ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Clock size={14} className="text-amber-500" />
                    <span className={`text-[10px] uppercase tracking-wider font-semibold ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Expiring</span>
                  </div>
                  <div className="text-2xl font-['Cabinet_Grotesk'] font-bold text-amber-600">{compliance?.expiring_count || 0}</div>
                  <div className={`text-[9px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>within 90 days</div>
                </button>
                <button onClick={() => setActiveTab('compliance')} className={`border-2 rounded-xl p-4 border-l-4 border-l-red-500 text-left cursor-pointer hover:shadow-md active:scale-[0.98] transition-all ${darkMode ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <XCircle size={14} className="text-red-500" />
                    <span className={`text-[10px] uppercase tracking-wider font-semibold ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Expired</span>
                  </div>
                  <div className="text-2xl font-['Cabinet_Grotesk'] font-bold text-red-600">{compliance?.expired_count || 0}</div>
                  <div className={`text-[9px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>need immediate action</div>
                </button>
                <button onClick={() => setActiveTab('documents')} className={`border-2 rounded-xl p-4 border-l-4 border-l-blue-500 text-left cursor-pointer hover:shadow-md active:scale-[0.98] transition-all ${darkMode ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <FileText size={14} className="text-blue-500" />
                    <span className={`text-[10px] uppercase tracking-wider font-semibold ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Docs Review</span>
                  </div>
                  <div className="text-2xl font-['Cabinet_Grotesk'] font-bold text-blue-600">{compliance?.pending_review_count || 0}</div>
                  <div className={`text-[9px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>awaiting review</div>
                </button>
              </div>
            </div>
          </motion.div>
        )}
        {activeTab === 'agents' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="admin-agents-tab">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div>
                <h1 className={`text-2xl font-['Cabinet_Grotesk'] font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Agent Management</h1>
                <p className={`text-sm ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{applications.length} application{applications.length !== 1 ? 's' : ''}</p>
              </div>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); }}>
                <SelectTrigger className="w-40 bg-white border-slate-200 text-slate-700 rounded-lg text-xs" data-testid="status-filter">
                  <Filter size={12} className="mr-1" /><SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="under_review">Under Review</SelectItem>
                  <SelectItem value="qualified">Qualified</SelectItem>
                  <SelectItem value="not_qualified">Not Qualified</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Bulk Action Toolbar */}
            {selectedApps.size > 0 && (
              <div className="bg-[#002FA7] text-white rounded-xl p-3 mb-3 flex flex-wrap items-center gap-3" data-testid="bulk-action-bar">
                <span className="text-xs font-semibold">{selectedApps.size} selected</span>
                <Button onClick={() => bulkAction('approved')} className="bg-emerald-500 hover:bg-emerald-600 text-white text-[10px] h-7 rounded-lg px-3" data-testid="bulk-approve-btn">
                  <CheckCircle2 size={12} className="mr-1" /> Approve All
                </Button>
                <Button onClick={() => setShowBulkReject(!showBulkReject)} className="bg-red-500 hover:bg-red-600 text-white text-[10px] h-7 rounded-lg px-3" data-testid="bulk-reject-btn">
                  <XCircle size={12} className="mr-1" /> Reject All
                </Button>
                <Button onClick={() => setSelectedApps(new Set())} variant="ghost" className="text-white/70 hover:text-white text-[10px] h-7 rounded-lg px-3">Clear</Button>
                {showBulkReject && (
                  <div className="w-full flex items-end gap-2 mt-1">
                    <div className="flex-1"><Textarea value={bulkRejectionReason} onChange={e => setBulkRejectionReason(e.target.value)} placeholder="Rejection reason (optional)..." className="text-xs bg-white/10 border-white/20 text-white placeholder:text-white/40 resize-none" rows={1} data-testid="bulk-rejection-reason" /></div>
                    <Button onClick={() => bulkAction('rejected')} className="bg-red-600 hover:bg-red-700 text-white text-[10px] h-8 rounded-lg" data-testid="bulk-confirm-reject">Confirm Reject</Button>
                  </div>
                )}
              </div>
            )}

            {/* Search & Filter */}
            <div className={`flex flex-col sm:flex-row gap-2 mb-4 p-3 border-2 rounded-xl ${darkMode ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200'}`} data-testid="agent-search-filter">
              <div className="flex-1 relative">
                <Search size={14} className={`absolute left-3 top-1/2 -translate-y-1/2 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                <Input value={appSearch} onChange={e => setAppSearch(e.target.value)} placeholder="Search name, email, license, location..." className={`pl-9 h-8 text-xs ${darkMode ? 'bg-slate-700 border-slate-600 text-white placeholder:text-slate-500' : ''}`} data-testid="agent-search-input" />
              </div>
              <select value={filterState} onChange={e => setFilterState(e.target.value)} className={`h-8 text-xs border rounded-lg px-2 ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-200 text-slate-600'}`} data-testid="filter-state">
                <option value="">All States</option>
                <option value="NY">New York</option>
                <option value="PA">Pennsylvania</option>
                <option value="FL">Florida</option>
              </select>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className={`h-8 text-xs border rounded-lg px-2 ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-200 text-slate-600'}`} data-testid="filter-status">
                <option value="">All Statuses</option>
                <option value="submitted">Submitted</option>
                <option value="under_review">Under Review</option>
                <option value="qualified">Qualified</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              <select value={filterSpec} onChange={e => setFilterSpec(e.target.value)} className={`h-8 text-xs border rounded-lg px-2 ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-200 text-slate-600'}`} data-testid="filter-spec">
                <option value="">All Types</option>
                <option value="residential">Residential</option>
                <option value="commercial">Commercial</option>
              </select>
              {(appSearch || filterState || filterStatus || filterSpec) && (
                <button onClick={() => { setAppSearch(''); setFilterState(''); setFilterStatus(''); setFilterSpec(''); }} className="text-[10px] text-red-500 hover:text-red-600 whitespace-nowrap px-2" data-testid="clear-filters">Clear Filters</button>
              )}
            </div>

            {/* Agent Cards */}
            <div className={`flex items-center gap-3 mb-3 px-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              <label className="flex items-center gap-2 text-[10px] cursor-pointer">
                <input type="checkbox" checked={selectedApps.size === filteredApps.length && filteredApps.length > 0} onChange={selectAllApps} className="rounded border-slate-300 text-[#002FA7] w-3.5 h-3.5" data-testid="select-all-checkbox" />
                Select All
              </label>
              <span className="text-[10px]">{filteredApps.length} of {applications.length} agent{applications.length !== 1 ? 's' : ''}</span>
            </div>
            {filteredApps.length === 0 ? (
              <div className={`border-2 border-dashed p-12 rounded-xl text-center ${darkMode ? 'border-slate-700 bg-slate-800/30' : 'border-slate-300 bg-slate-50'}`}>
                <FileText size={40} className={`mx-auto mb-4 ${darkMode ? 'text-slate-600' : 'text-slate-300'}`} />
                <p className={`text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{applications.length === 0 ? 'No applications yet' : 'No agents match your filters'}</p>
                {(appSearch || filterState || filterStatus || filterSpec) && (
                  <button onClick={() => { setAppSearch(''); setFilterState(''); setFilterStatus(''); setFilterSpec(''); }} className="mt-2 text-xs text-[#002FA7] hover:underline">Clear all filters</button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredApps.map(app => {
                  const statusBorder = {
                    approved: 'border-l-emerald-500', rejected: 'border-l-red-500',
                    under_review: 'border-l-blue-500', qualified: 'border-l-violet-500',
                    not_qualified: 'border-l-amber-500', submitted: 'border-l-slate-400',
                  }[app.status] || 'border-l-slate-300';
                  return (
                  <div key={app.id} className={`border-2 border-l-4 ${statusBorder} rounded-xl transition-all ${selectedApps.has(app.id) ? 'border-[#002FA7] bg-blue-50/40 dark:bg-blue-900/15 shadow-md' : darkMode ? 'bg-slate-800/60 border-slate-700 hover:border-slate-500 hover:shadow-lg' : 'bg-white border-slate-200 hover:border-[#002FA7]/30 hover:shadow-lg'}`} data-testid={`admin-app-${app.id}`}>
                    {/* Profile Card Layout */}
                    <div className="hidden lg:block">
                      <div className="flex items-start gap-4 p-5">
                        {/* Checkbox + Avatar */}
                        <div className="flex items-start gap-3 shrink-0">
                          <input type="checkbox" checked={selectedApps.has(app.id)} onChange={() => toggleSelectApp(app.id)} className="rounded border-slate-300 text-[#002FA7] w-3.5 h-3.5 mt-1" data-testid={`select-app-${app.id}`} />
                          <button onClick={() => setViewingApp(app)} className={`w-12 h-12 rounded-xl flex items-center justify-center text-sm font-bold shrink-0 cursor-pointer transition-transform hover:scale-105 ${
                            app.status === 'approved' ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-200' :
                            app.status === 'rejected' ? 'bg-red-100 text-red-600 ring-2 ring-red-200' :
                            'bg-[#002FA7]/10 text-[#002FA7] ring-2 ring-[#002FA7]/20'
                          }`} data-testid={`view-app-avatar-${app.id}`}>
                            {(app.first_name || '?')[0]}{(app.last_name || '?')[0]}
                          </button>
                        </div>
                        {/* Main info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <button onClick={() => setViewingApp(app)} className={`text-sm font-semibold hover:underline cursor-pointer ${darkMode ? 'text-white hover:text-blue-300' : 'text-slate-900 hover:text-[#002FA7]'}`} data-testid={`view-app-name-${app.id}`}>{app.first_name} {app.last_name}</button>
                            {statusBadge(app.status)}
                            {icaBadge(app.ica_signed)}
                            {app.qualification_flags?.auto_qualified && <Badge className="text-[7px] bg-amber-50 text-amber-600 border-amber-200">Auto-Qualified</Badge>}
                            {app.experience_years >= 5 && <Badge className="text-[7px] bg-blue-50 text-blue-600 border-blue-200">{app.experience_years}yr Exp</Badge>}
                          </div>
                          <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-xs ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            <span className="flex items-center gap-1.5"><Mail size={11} className="text-[#002FA7]" /> {app.email}</span>
                            {app.phone && <span className="flex items-center gap-1.5"><Phone size={11} className="text-emerald-500" /> {app.phone}</span>}
                            <span className="flex items-center gap-1.5"><MapPin size={11} className="text-amber-500" /> {app.preferred_location || 'N/A'}</span>
                            {app.license_number && <span className="flex items-center gap-1.5"><FileSignature size={11} className="text-violet-500" /> {app.license_number} {app.license_state && <Badge className="text-[7px] ml-0.5 bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-700 dark:text-slate-400 dark:border-slate-600">{app.license_state}</Badge>}</span>}
                          </div>
                          <div className={`flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-2 pt-2 border-t text-[10px] ${darkMode ? 'text-slate-500 border-slate-700' : 'text-slate-400 border-slate-100'}`}>
                            <span className="capitalize">{(app.specialization || 'N/A').replace(/_/g, ' ')}</span>
                            <span className={`w-1 h-1 rounded-full ${darkMode ? 'bg-slate-600' : 'bg-slate-300'}`} />
                            <span>Applied {app.created_at ? new Date(app.created_at).toLocaleDateString() : 'N/A'}</span>
                            {app.experience_years > 0 && <><span className={`w-1 h-1 rounded-full ${darkMode ? 'bg-slate-600' : 'bg-slate-300'}`} /><span>{app.experience_years} yrs exp</span></>}
                          </div>
                        </div>
                        {/* Right side: Split + Source + Actions */}
                        <div className={`flex items-center gap-3 shrink-0 pl-4 border-l-2 ${darkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                          <div className={`text-center px-3 py-1.5 rounded-lg ${darkMode ? 'bg-slate-700/50' : 'bg-[#002FA7]/5'}`}>
                            {editingSplit === app.id ? (
                              <div className="flex items-center gap-1">
                                <Input type="number" value={editSplitVal} onChange={(e) => setEditSplitVal(parseFloat(e.target.value))}
                                  className="w-16 h-7 text-xs border-slate-200 rounded" data-testid={`split-input-${app.id}`} />
                                <button onClick={() => updateSplit(app.id, editSplitVal)} className="text-emerald-600 text-xs font-medium">Save</button>
                              </div>
                            ) : (
                              <button onClick={() => { setEditingSplit(app.id); setEditSplitVal(app.commission_split || 90); }}
                                className="text-lg font-['Cabinet_Grotesk'] font-bold text-[#002FA7] hover:underline" data-testid={`split-${app.id}`}>
                                {app.commission_split || 90}%
                              </button>
                            )}
                            <div className={`text-[8px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Split</div>
                          </div>
                          <Select value={app.deal_source || 'unassigned'} onValueChange={(v) => updateDealSource(app.id, v)}>
                            <SelectTrigger className={`h-7 w-28 text-[10px] rounded ${darkMode ? 'border-slate-600 bg-slate-700 text-slate-300' : 'border-slate-200'}`} data-testid={`source-${app.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className={darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
                              {Object.entries(DEAL_SOURCE_LABELS).map(([k, v]) => (
                                <SelectItem key={k} value={k}>{v}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" size="sm" className={`h-8 text-[10px] font-medium rounded-lg gap-1 ${darkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-[#002FA7]/20 text-[#002FA7] hover:bg-blue-50'}`} data-testid={`action-${app.id}`}>
                                Actions <ChevronDown size={10} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className={darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
                              <DropdownMenuItem onClick={() => updateStatus(app.id, 'under_review')} className="text-blue-600 text-xs" data-testid={`review-${app.id}`}>
                                <Eye size={12} className="mr-2" /> Under Review
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => updateStatus(app.id, 'qualified')} className="text-emerald-600 text-xs" data-testid={`qualify-${app.id}`}>
                                <CheckCircle2 size={12} className="mr-2" /> Qualified
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => updateStatus(app.id, 'not_qualified')} className="text-amber-600 text-xs" data-testid={`not-qualify-${app.id}`}>
                                <AlertTriangle size={12} className="mr-2" /> Not Qualified
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => updateStatus(app.id, 'approved')} className="text-emerald-600 text-xs" data-testid={`approve-${app.id}`}>
                                <CheckCircle2 size={12} className="mr-2" /> Approve
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleReject(app.id)} className="text-red-500 text-xs" data-testid={`reject-${app.id}`}>
                                <XCircle size={12} className="mr-2" /> Reject
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </div>

                    {/* Rejection reason input */}
                    {rejectingAppId === app.id && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-2" data-testid={`rejection-form-${app.id}`}>
                        <label className="text-[10px] font-semibold text-red-700 uppercase tracking-wider block mb-1.5">Rejection Reason (sent to applicant)</label>
                        <Textarea
                          value={rejectionReason}
                          onChange={e => setRejectionReason(e.target.value)}
                          placeholder="e.g., License not active, insufficient transaction history, missing state license..."
                          className="text-xs border-red-200 bg-white mb-2 resize-none"
                          rows={2}
                          data-testid={`rejection-reason-${app.id}`}
                        />
                        <div className="flex gap-2">
                          <Button onClick={() => updateStatus(app.id, 'rejected', rejectionReason)} className="bg-red-600 hover:bg-red-700 text-white text-[10px] h-7 rounded" data-testid={`confirm-reject-${app.id}`}>
                            Confirm Rejection
                          </Button>
                          <Button variant="ghost" onClick={() => { setRejectingAppId(null); setRejectionReason(''); }} className="text-[10px] h-7 text-slate-500">Cancel</Button>
                        </div>
                      </div>
                    )}

                    {/* Mobile card */}
                    <div className="lg:hidden space-y-3 p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <button onClick={() => setViewingApp(app)} className={`text-sm font-medium hover:underline ${darkMode ? 'text-white hover:text-blue-300' : 'text-slate-800 hover:text-[#002FA7]'}`} data-testid={`view-app-mobile-${app.id}`}>{app.first_name} {app.last_name}</button>
                          <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-400'}`}>{app.email} · {app.phone}</div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {statusBadge(app.status)}
                          {icaBadge(app.ica_signed)}
                        </div>
                      </div>
                      <div className={`flex flex-wrap gap-2 text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        <span>License: {app.license_number || '—'} {app.license_state && `(${app.license_state})`}</span>
                        <span>·</span>
                        <span>{app.preferred_location}</span>
                        <span>·</span>
                        <span>Split: {app.commission_split || 90}%</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select value={app.deal_source || 'unassigned'} onValueChange={(v) => updateDealSource(app.id, v)}>
                          <SelectTrigger className={`h-7 text-xs rounded flex-1 ${darkMode ? 'border-slate-600 bg-slate-700 text-slate-300' : 'border-slate-200'}`}><SelectValue /></SelectTrigger>
                          <SelectContent className={darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
                            {Object.entries(DEAL_SOURCE_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                          </SelectContent>
                        </Select>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" className={`h-7 rounded text-[10px] ${darkMode ? 'border-slate-600 text-slate-300' : 'border-slate-200 text-slate-500'}`}>Actions</Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className={darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
                            <DropdownMenuItem onClick={() => updateStatus(app.id, 'under_review')} className="text-blue-600 text-xs"><Eye size={12} className="mr-2" /> Under Review</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => updateStatus(app.id, 'qualified')} className="text-emerald-600 text-xs"><CheckCircle2 size={12} className="mr-2" /> Qualified</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => updateStatus(app.id, 'not_qualified')} className="text-amber-600 text-xs"><XCircle size={12} className="mr-2" /> Not Qualified</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => updateStatus(app.id, 'approved')} className="text-emerald-600 text-xs"><CheckCircle2 size={12} className="mr-2" /> Approve</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleReject(app.id)} className="text-red-500 text-xs"><XCircle size={12} className="mr-2" /> Reject</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>

                    <div className="text-[10px] text-slate-300 dark:text-slate-600 mt-2 px-5">Applied: {new Date(app.created_at).toLocaleDateString()}</div>
                    {/* Qualifying Questions */}
                    {app.has_active_license && (
                      <div className={`mt-3 mx-5 mb-4 pt-3 border-t ${darkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-[9px] tracking-wider uppercase text-slate-400 font-semibold">Qualifying Responses</span>
                          {app.qualification_flags?.auto_qualified && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[9px] font-semibold text-emerald-700" data-testid={`auto-qualified-${app.id}`}>
                              <CheckCircle2 size={10} /> Auto-Qualified
                            </span>
                          )}
                          {app.qualification_flags && !app.qualification_flags.auto_qualified && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-[9px] font-semibold text-amber-700">
                              <AlertTriangle size={10} /> Needs Review
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
                          <div><span className={`block ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>License</span><span className={`font-medium ${app.qualification_flags?.has_active_license ? 'text-emerald-600' : 'text-red-500'}`}>{app.has_active_license}</span></div>
                          <div><span className={`block ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Sales (Yr)</span><span className={`font-medium ${darkMode ? 'text-white' : 'text-slate-700'}`}>{app.sales_transactions_last_year || 0}</span></div>
                          <div><span className={`block ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Rentals (Yr)</span><span className={`font-medium ${darkMode ? 'text-white' : 'text-slate-700'}`}>{app.rental_transactions_last_year || 0}</span></div>
                          <div><span className={`block ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Lead Gen</span><span className={`font-medium capitalize ${darkMode ? 'text-white' : 'text-slate-700'}`}>{(app.lead_generation_method || '—').replace(/_/g, ' ')}</span></div>
                          <div><span className={`block ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Mkt Budget</span><span className={`font-medium ${darkMode ? 'text-white' : 'text-slate-700'}`}>{app.monthly_marketing_budget || '—'}</span></div>
                        </div>
                        {app.rejection_reason && (
                          <div className="mt-2 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            <span className="text-[9px] text-red-400 font-semibold uppercase">Rejection Reason:</span>
                            <p className="text-xs text-red-600 mt-0.5">{app.rejection_reason}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}

        {/* ═══════════ COMPLIANCE TAB ═══════════ */}
        {activeTab === 'compliance' && (
          <ComplianceTab api={API} darkMode={darkMode} />
        )}

        {/* ═══════════ DOCUMENTS TAB ═══════════ */}
        {activeTab === 'documents' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="admin-documents-tab">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div>
                <h1 className={`text-2xl font-['Cabinet_Grotesk'] font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Document Review</h1>
                <p className={`text-sm ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{adminDocs.length} document{adminDocs.length !== 1 ? 's' : ''} uploaded by agents</p>
              </div>
              <Select value={docFilter} onValueChange={setDocFilter}>
                <SelectTrigger className="w-44 bg-white border-slate-200 text-slate-700 rounded-lg text-xs" data-testid="doc-review-filter">
                  <Filter size={12} className="mr-1" /><SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="all">All Documents</SelectItem>
                  <SelectItem value="pending_review">Pending Review</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(() => {
              const filtered = docFilter === 'all' ? adminDocs : adminDocs.filter(d => d.review_status === docFilter);
              if (filtered.length === 0) return (
                <div className="bg-white border border-slate-200 p-12 rounded-xl text-center">
                  <FileText size={40} className="text-slate-200 mx-auto mb-4" />
                  <p className="text-slate-400">No documents found</p>
                </div>
              );
              return (
                <div className="space-y-2">
                  {filtered.map(doc => {
                    const reviewStatus = doc.review_status || 'pending_review';
                    const statusColors = {
                      pending_review: 'bg-amber-50 text-amber-600 border-amber-200',
                      approved: 'bg-emerald-50 text-emerald-600 border-emerald-200',
                      rejected: 'bg-red-50 text-red-500 border-red-200',
                    };
                    return (
                      <div key={doc.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`admin-doc-${doc.id}`}>
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <FileText size={16} className="text-slate-400 shrink-0" />
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-slate-800 truncate">{doc.original_filename}</div>
                              <div className="text-[10px] text-slate-400">
                                {doc.agent_name || doc.agent_email} · {(doc.category || 'general').replace(/_/g, ' ')} · {new Date(doc.created_at).toLocaleDateString()}
                                {doc.expiration_date && <span className="ml-1">· Expires: {new Date(doc.expiration_date).toLocaleDateString()}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Badge variant="outline" className={`text-[9px] ${statusColors[reviewStatus] || ''}`}>{reviewStatus.replace(/_/g, ' ')}</Badge>
                            {reviewStatus === 'pending_review' && (
                              <>
                                <Button size="sm" onClick={() => reviewDocument(doc.id, 'approved')}
                                  className="h-7 bg-emerald-600 text-white hover:bg-emerald-700 rounded text-[10px]" data-testid={`approve-doc-${doc.id}`}>
                                  <CheckCircle2 size={10} className="mr-1" /> Approve
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => reviewDocument(doc.id, 'rejected')}
                                  className="h-7 border-red-200 text-red-500 hover:bg-red-50 rounded text-[10px]" data-testid={`reject-doc-${doc.id}`}>
                                  <XCircle size={10} className="mr-1" /> Reject
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </motion.div>
        )}

        {/* ═══════════ TEAMS TAB ═══════════ */}
        {activeTab === 'teams' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="admin-teams-tab">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-['Cabinet_Grotesk'] font-bold text-slate-900">Team Management</h1>
                <p className="text-sm text-slate-400">{teams.length} team{teams.length !== 1 ? 's' : ''}</p>
              </div>
              <Button onClick={() => setShowTeamForm(!showTeamForm)} className="bg-[#002FA7] text-white hover:bg-[#002075] rounded-lg text-xs" data-testid="create-team-btn">
                <Plus size={14} className="mr-1" /> New Team
              </Button>
            </div>

            {/* Create Team Form */}
            {showTeamForm && (
              <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6" data-testid="team-form">
                <h3 className="text-sm font-semibold text-slate-800 mb-3">Create New Team</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Team Name</Label>
                    <Input value={newTeamName} onChange={(e) => setNewTeamName(e.target.value)} placeholder="e.g. Manhattan Elite"
                      className="border-slate-200 rounded-lg text-sm" data-testid="team-name-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Team Leader Email</Label>
                    <Input value={newTeamLeader} onChange={(e) => setNewTeamLeader(e.target.value)} placeholder="leader@example.com"
                      className="border-slate-200 rounded-lg text-sm" data-testid="team-leader-input" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={createTeam} className="bg-[#002FA7] text-white hover:bg-[#002075] rounded-lg text-xs" data-testid="save-team-btn">Create Team</Button>
                  <Button variant="outline" onClick={() => setShowTeamForm(false)} className="border-slate-200 text-slate-500 rounded-lg text-xs">Cancel</Button>
                </div>
              </div>
            )}

            {teams.length === 0 && !showTeamForm ? (
              <div className="bg-white border border-slate-200 p-12 rounded-xl text-center">
                <Building2 size={40} className="text-slate-200 mx-auto mb-4" />
                <p className="text-slate-400">No teams created yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {teams.map(team => (
                  <div key={team.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`team-${team.id}`}>
                    <div className="px-5 py-4 flex items-center justify-between border-b border-slate-100">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-semibold text-slate-800">{team.name}</h3>
                          <Badge variant="outline" className="text-[9px] border-[#002FA7]/20 text-[#002FA7]">{team.member_count || 0} members</Badge>
                        </div>
                        <div className="text-xs text-slate-400 mt-0.5">Leader: {team.leader_email}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => setAddMemberTeamId(addMemberTeamId === team.id ? null : team.id)}
                          className="h-7 border-slate-200 text-xs rounded" data-testid={`add-member-${team.id}`}>
                          <Plus size={12} className="mr-1" /> Add Member
                        </Button>
                        <button onClick={() => deleteTeam(team.id)} className="text-slate-300 hover:text-red-400 transition-colors p-1" data-testid={`delete-team-${team.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>

                    {/* Add Member Form */}
                    {addMemberTeamId === team.id && (
                      <div className="px-5 py-3 bg-slate-50 border-b border-slate-100 flex items-end gap-3" data-testid={`add-member-form-${team.id}`}>
                        <div className="flex-1">
                          <Label className="text-[10px] text-slate-400 mb-1 block">Agent Email</Label>
                          <Input value={newMemberEmail} onChange={(e) => setNewMemberEmail(e.target.value)} placeholder="agent@email.com"
                            className="border-slate-200 rounded text-xs h-8" data-testid={`member-email-${team.id}`} />
                        </div>
                        <div className="w-24">
                          <Label className="text-[10px] text-slate-400 mb-1 block">Split %</Label>
                          <Input type="number" value={newMemberSplit} onChange={(e) => setNewMemberSplit(parseFloat(e.target.value) || 90)}
                            className="border-slate-200 rounded text-xs h-8" data-testid={`member-split-${team.id}`} />
                        </div>
                        <div className="w-24">
                          <Label className="text-[10px] text-slate-400 mb-1 block">Override %</Label>
                          <Input type="number" value={0} placeholder="0"
                            className="border-slate-200 rounded text-xs h-8" data-testid={`member-override-${team.id}`} />
                        </div>
                        <Button onClick={() => addTeamMember(team.id)} className="bg-[#002FA7] text-white hover:bg-[#002075] rounded text-xs h-8" data-testid={`confirm-add-${team.id}`}>Add</Button>
                      </div>
                    )}

                    {/* Members List */}
                    {team.members && team.members.length > 0 && (
                      <div>
                        <div className="grid grid-cols-12 gap-2 px-5 py-2 text-[9px] tracking-wider uppercase text-slate-400 font-semibold bg-slate-50/50">
                          <div className="col-span-4">Agent</div>
                          <div className="col-span-2">Role</div>
                          <div className="col-span-2">Commission Split</div>
                          <div className="col-span-2">Override</div>
                          <div className="col-span-2">Actions</div>
                        </div>
                        {team.members.map(member => (
                          <div key={member.id} className="grid grid-cols-12 gap-2 px-5 py-3 items-center border-t border-slate-50 text-sm" data-testid={`member-${member.id}`}>
                            <div className="col-span-4 text-xs text-slate-600">{member.agent_email}</div>
                            <div className="col-span-2">
                              <Badge variant="outline" className={`text-[9px] ${member.role === 'leader' ? 'border-[#002FA7]/20 text-[#002FA7]' : 'border-slate-200 text-slate-400'}`}>
                                {member.role}
                              </Badge>
                            </div>
                            <div className="col-span-2">
                              {editingSplit === member.id ? (
                                <div className="flex items-center gap-1">
                                  <Input type="number" value={editSplitVal} onChange={(e) => setEditSplitVal(parseFloat(e.target.value))}
                                    className="w-16 h-6 text-xs border-slate-200 rounded" />
                                  <button onClick={() => updateMemberSplit(team.id, member.id, editSplitVal)} className="text-emerald-600 text-[10px] font-medium">Save</button>
                                </div>
                              ) : (
                                <button onClick={() => { setEditingSplit(member.id); setEditSplitVal(member.commission_split); }}
                                  className="text-xs font-semibold text-[#002FA7] hover:underline">
                                  {member.commission_split}%
                                </button>
                              )}
                            </div>
                            <div className="col-span-2 text-xs text-slate-500">{member.override_percentage || 0}%</div>
                            <div className="col-span-2">
                              <button onClick={() => removeMember(team.id, member.id)} className="text-slate-300 hover:text-red-400 transition-colors" data-testid={`remove-${member.id}`}>
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* ═══════════ REFERRALS TAB ═══════════ */}
        {activeTab === 'referrals' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="admin-referrals-tab">
            <h1 className="text-2xl font-['Cabinet_Grotesk'] font-bold text-slate-900 mb-2">Referral Leaderboard</h1>
            <p className="text-sm text-slate-400 mb-6">Agents ranked by referral performance.</p>

            {referralLeaderboard.length === 0 ? (
              <div className="bg-white border border-slate-200 p-12 rounded-xl text-center">
                <Share2 size={40} className="text-slate-200 mx-auto mb-4" />
                <p className="text-slate-400">No referrals tracked yet. Agents can share referral links from their dashboard.</p>
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="grid grid-cols-12 gap-3 px-5 py-2 text-[10px] tracking-wider uppercase text-slate-400 font-semibold border-b border-slate-200">
                  <div className="col-span-1">Rank</div>
                  <div className="col-span-3">Agent</div>
                  <div className="col-span-3">Email</div>
                  <div className="col-span-2">Referral Code</div>
                  <div className="col-span-1">Total</div>
                  <div className="col-span-2">Approved</div>
                </div>
                {referralLeaderboard.map((agent, idx) => (
                  <div key={agent.user_id} className="grid grid-cols-12 gap-3 px-5 py-3 items-center border-b border-slate-50" data-testid={`leaderboard-${idx}`}>
                    <div className="col-span-1">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
                        ${idx === 0 ? 'bg-amber-100 text-amber-700' : ''}
                        ${idx === 1 ? 'bg-slate-100 text-slate-600' : ''}
                        ${idx === 2 ? 'bg-orange-50 text-orange-600' : ''}
                        ${idx > 2 ? 'bg-slate-50 text-slate-400' : ''}
                      `}>{idx + 1}</span>
                    </div>
                    <div className="col-span-3 text-sm font-medium text-slate-800">{agent.name}</div>
                    <div className="col-span-3 text-xs text-slate-500">{agent.email}</div>
                    <div className="col-span-2 font-mono text-xs text-[#002FA7]">{agent.code}</div>
                    <div className="col-span-1 text-sm font-semibold text-slate-800">{agent.total_referrals}</div>
                    <div className="col-span-2 flex items-center gap-2">
                      <span className="text-sm font-semibold text-emerald-600">{agent.approved_referrals}</span>
                      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${agent.total_referrals > 0 ? (agent.approved_referrals / agent.total_referrals) * 100 : 0}%` }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* ═══════════ ANALYTICS TAB ═══════════ */}
        {activeTab === 'analytics' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="admin-analytics-tab">
            <h1 className="text-2xl font-['Cabinet_Grotesk'] font-bold text-slate-900 mb-2">Performance Analytics</h1>
            <p className="text-sm text-slate-400 mb-6">Agent performance, deal metrics, and recruitment insights.</p>

            {/* KPI Cards */}
            {analyticsOverview && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <StatCard label="Active Agents" value={analyticsOverview.active_agents} icon={Users} sub={`${analyticsOverview.total_agents} total`} />
                <StatCard label="Closed Deals" value={analyticsOverview.total_deals} icon={CheckCircle2} sub={`${analyticsOverview.month_deals} this month`} />
                <StatCard label="Total Volume" value={`$${(analyticsOverview.total_volume / 1000000).toFixed(1)}M`} icon={DollarSign} sub={`$${(analyticsOverview.month_volume / 1000).toFixed(0)}K this month`} />
                <StatCard label="Brokerage Revenue" value={`$${analyticsOverview.brokerage_revenue.toLocaleString()}`} icon={TrendingUp} sub={`${analyticsOverview.conversion_rate}% app conversion`} />
              </div>
            )}

            {/* Deal Type Breakdown */}
            {dealTypeBreakdown.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
                <h3 className="text-sm font-semibold text-slate-800 mb-4">Deal Type Breakdown</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {dealTypeBreakdown.map(dt => (
                    <div key={dt.deal_type} className="flex items-center justify-between bg-slate-50 p-4 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-slate-800">{dt.deal_type === 'commercial_sale' ? 'Commercial Sales' : dt.deal_type === 'commercial_lease' ? 'Commercial Leases' : dt.deal_type === 'residential_sale' ? 'Residential Sales' : 'Residential Rentals'}</p>
                        <p className="text-xs text-slate-400">{dt.count} deals</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-[#002FA7]">${(dt.volume / 1000000).toFixed(1)}M</p>
                        <p className="text-xs text-emerald-600">${dt.commission.toLocaleString()} earned</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Agent Leaderboard */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Trophy size={16} className="text-amber-500" /> Agent Leaderboard</h3>
                <Select value={leaderboardPeriod} onValueChange={setLeaderboardPeriod}>
                  <SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="year">This Year</SelectItem>
                    <SelectItem value="quarter">This Quarter</SelectItem>
                    <SelectItem value="month">This Month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {agentLeaderboard.length === 0 ? (
                <p className="text-slate-400 text-sm text-center py-8">No closed deals yet for this period.</p>
              ) : (
                <div className="space-y-2">
                  {agentLeaderboard.slice(0, 10).map((agent, idx) => (
                    <div key={agent.user_id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors" data-testid={`analytics-agent-${idx}`}>
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${idx === 0 ? 'bg-amber-100 text-amber-700' : idx === 1 ? 'bg-slate-100 text-slate-600' : idx === 2 ? 'bg-orange-50 text-orange-600' : 'bg-slate-50 text-slate-400'}`}>{idx + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{agent.name}</p>
                        <p className="text-xs text-slate-400">{agent.deals} deals</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-slate-800">${agent.volume.toLocaleString()}</p>
                        <p className="text-xs text-emerald-600">${agent.commission.toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Deal Trends */}
            {dealTrends.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
                <h3 className="text-sm font-semibold text-slate-800 mb-4">Monthly Deal Trends</h3>
                <div className="overflow-x-auto">
                  <div className="flex items-end gap-2 h-40 min-w-fit">
                    {dealTrends.map(m => {
                      const maxVol = Math.max(...dealTrends.map(d => d.volume), 1);
                      const h = Math.max(8, (m.volume / maxVol) * 140);
                      return (
                        <div key={m.month} className="flex flex-col items-center gap-1 min-w-[60px]">
                          <span className="text-[10px] text-slate-500 font-medium">${(m.volume / 1000).toFixed(0)}K</span>
                          <div className="w-8 rounded-t-md bg-[#002FA7]/80 hover:bg-[#002FA7] transition-colors" style={{ height: `${h}px` }} title={`${m.closed} closed deals`} />
                          <span className="text-[10px] text-slate-400">{m.month.slice(5)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Recruitment Funnel */}
            {recruitmentFunnel && (
              <div className="bg-white border border-slate-200 rounded-xl p-6">
                <h3 className="text-sm font-semibold text-slate-800 mb-4">Recruitment Funnel</h3>
                <div className="space-y-3">
                  {[
                    { label: 'Applications Submitted', value: recruitmentFunnel.submitted, color: 'bg-slate-500' },
                    { label: 'Pending Review', value: recruitmentFunnel.pending_review, color: 'bg-amber-500' },
                    { label: 'Approved', value: recruitmentFunnel.approved, color: 'bg-emerald-500' },
                    { label: 'ICA Signed', value: recruitmentFunnel.ica_signed, color: 'bg-blue-500' },
                    { label: 'Fully Onboarded', value: recruitmentFunnel.fully_onboarded, color: 'bg-[#002FA7]' },
                  ].map(step => {
                    const max = recruitmentFunnel.submitted || 1;
                    const pct = Math.round((step.value / max) * 100);
                    return (
                      <div key={step.label} className="flex items-center gap-3">
                        <span className="text-xs text-slate-500 w-36 text-right">{step.label}</span>
                        <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full ${step.color} rounded-full transition-all duration-500 flex items-center justify-end pr-2`} style={{ width: `${Math.max(pct, 2)}%` }}>
                            {pct > 10 && <span className="text-[10px] font-bold text-white">{step.value}</span>}
                          </div>
                        </div>
                        {pct <= 10 && <span className="text-xs font-semibold text-slate-600 w-8">{step.value}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Application Volume Trends */}
            {appTrends.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl p-6">
                <h3 className="text-sm font-semibold text-slate-800 mb-4">Application Volume & Auto-Qualify Trends</h3>
                <div className="overflow-x-auto">
                  <div className="flex items-end gap-1.5 h-44 min-w-fit mb-2">
                    {appTrends.map(m => {
                      const maxTotal = Math.max(...appTrends.map(d => d.total), 1);
                      const h = Math.max(8, (m.total / maxTotal) * 140);
                      const aqH = Math.max(0, (m.auto_qualified / maxTotal) * 140);
                      return (
                        <div key={m.month} className="flex flex-col items-center gap-0.5 min-w-[56px]" title={`${m.total} apps, ${m.auto_qualified} auto-qualified, ${m.approval_rate}% approved`}>
                          <span className="text-[9px] text-slate-500 font-medium">{m.total}</span>
                          <div className="relative w-8">
                            <div className="w-full rounded-t-md bg-slate-200" style={{ height: `${h}px` }} />
                            <div className="absolute bottom-0 left-0 w-full rounded-t-md bg-[#002FA7]/80" style={{ height: `${aqH}px` }} />
                          </div>
                          <span className="text-[9px] text-slate-400">{m.month.slice(5)}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-4 text-[10px] text-slate-400 mt-1 border-t border-slate-100 pt-2">
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-slate-200" /> Total Apps</span>
                    <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#002FA7]/80" /> Auto-Qualified</span>
                  </div>
                </div>
                {/* Monthly stats table */}
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {appTrends.slice(-4).reverse().map(m => (
                    <div key={m.month} className="bg-slate-50 rounded-lg p-3">
                      <div className="text-[10px] text-slate-400 font-semibold mb-1">{m.month}</div>
                      <div className="text-lg font-['Cabinet_Grotesk'] font-bold text-slate-800">{m.total}</div>
                      <div className="text-[10px] text-slate-400 mt-1">{m.approval_rate}% approved &middot; {m.auto_qualify_rate}% auto-qualified</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
        {activeTab === 'blog' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="admin-blog-tab">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-['Cabinet_Grotesk'] font-bold text-slate-900 mb-1">Blog Management</h1>
                <p className="text-sm text-slate-400">Create and manage SEO content for the blog.</p>
              </div>
              {!showBlogEditor && (
                <Button onClick={() => { resetBlogForm(); setShowBlogEditor(true); }} className="bg-[#002FA7] hover:bg-[#002075] text-white rounded-lg text-xs gap-1.5" data-testid="new-blog-post-btn">
                  <Plus size={14} /> New Post
                </Button>
              )}
            </div>

            {showBlogEditor ? (
              <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="blog-editor">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">{editingPost ? 'Edit Post' : 'New Post'}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Title *</Label>
                    <Input value={blogForm.title} onChange={e => { setBlogForm(f => ({ ...f, title: e.target.value, slug: editingPost ? f.slug : generateSlug(e.target.value) })); }} placeholder="Article title" data-testid="blog-title-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">URL Slug *</Label>
                    <Input value={blogForm.slug} onChange={e => setBlogForm(f => ({ ...f, slug: e.target.value }))} placeholder="url-slug" data-testid="blog-slug-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Category</Label>
                    <Select value={blogForm.category} onValueChange={v => setBlogForm(f => ({ ...f, category: v }))}>
                      <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['market-insights', 'investment-strategies', 'agent-success', 'industry-news', 'commercial-trends', 'leasing', 'general'].map(c => (
                          <SelectItem key={c} value={c}>{c.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Tags (comma-separated)</Label>
                    <Input value={blogForm.tags} onChange={e => setBlogForm(f => ({ ...f, tags: e.target.value }))} placeholder="commercial, multifamily, nyc" data-testid="blog-tags-input" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-slate-500 mb-1 block">Excerpt</Label>
                    <Input value={blogForm.excerpt} onChange={e => setBlogForm(f => ({ ...f, excerpt: e.target.value }))} placeholder="Brief summary for cards and SEO" data-testid="blog-excerpt-input" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-slate-500 mb-1 block">Content (HTML) *</Label>
                    <Textarea value={blogForm.content} onChange={e => setBlogForm(f => ({ ...f, content: e.target.value }))} rows={10} placeholder="<p>Your article content...</p>" className="font-mono text-xs" data-testid="blog-content-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Featured Image URL</Label>
                    <Input value={blogForm.featured_image} onChange={e => setBlogForm(f => ({ ...f, featured_image: e.target.value }))} placeholder="https://..." data-testid="blog-image-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Status</Label>
                    <Select value={blogForm.status} onValueChange={v => setBlogForm(f => ({ ...f, status: v }))}>
                      <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="published">Published</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Meta Title (SEO)</Label>
                    <Input value={blogForm.meta_title} onChange={e => setBlogForm(f => ({ ...f, meta_title: e.target.value }))} placeholder="SEO title" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Meta Description (SEO)</Label>
                    <Input value={blogForm.meta_description} onChange={e => setBlogForm(f => ({ ...f, meta_description: e.target.value }))} placeholder="SEO description" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={saveBlogPost} className="bg-[#002FA7] hover:bg-[#002075] text-white text-xs rounded-lg" data-testid="save-blog-btn">{editingPost ? 'Update' : 'Create'} Post</Button>
                  <Button variant="outline" onClick={resetBlogForm} className="text-xs rounded-lg" data-testid="cancel-blog-btn">Cancel</Button>
                </div>
              </div>
            ) : blogPosts.length === 0 ? (
              <div className="bg-white border border-slate-200 p-12 rounded-xl text-center">
                <PenSquare size={40} className="text-slate-200 mx-auto mb-4" />
                <p className="text-slate-400 mb-4">No blog posts yet. Create your first article to boost SEO.</p>
                <Button onClick={() => setShowBlogEditor(true)} className="bg-[#002FA7] hover:bg-[#002075] text-white rounded-lg text-xs gap-1.5">
                  <Plus size={14} /> Create First Post
                </Button>
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="grid grid-cols-12 gap-3 px-5 py-2 text-[10px] tracking-wider uppercase text-slate-400 font-semibold border-b border-slate-200">
                  <div className="col-span-4">Title</div>
                  <div className="col-span-2">Category</div>
                  <div className="col-span-1">Status</div>
                  <div className="col-span-1">Views</div>
                  <div className="col-span-2">Date</div>
                  <div className="col-span-2">Actions</div>
                </div>
                {blogPosts.map(post => (
                  <div key={post.id} className="grid grid-cols-12 gap-3 px-5 py-3 items-center border-b border-slate-50 hover:bg-slate-50/50 transition-colors" data-testid={`blog-row-${post.id}`}>
                    <div className="col-span-4 text-sm font-medium text-slate-800 truncate">{post.title}</div>
                    <div className="col-span-2"><Badge variant="secondary" className="text-[10px]">{post.category}</Badge></div>
                    <div className="col-span-1">
                      <Badge className={post.status === 'published' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-500 border-slate-200'}>
                        {post.status}
                      </Badge>
                    </div>
                    <div className="col-span-1 text-xs text-slate-500">{post.views || 0}</div>
                    <div className="col-span-2 text-xs text-slate-400">{(post.published_at || post.created_at || '').slice(0, 10)}</div>
                    <div className="col-span-2 flex gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setEditingPost(post.id); setBlogForm({ ...post, tags: (post.tags || []).join(', ') }); setShowBlogEditor(true); }} data-testid={`edit-blog-${post.id}`}>
                        <Edit2 size={12} />
                      </Button>
                      {post.status === 'published' && (
                        <a href={`/blog/${post.slug}`} target="_blank" rel="noreferrer">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0"><Eye size={12} /></Button>
                        </a>
                      )}
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => deleteBlogPost(post.id)} data-testid={`delete-blog-${post.id}`}>
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* ═══════════ RECRUITER TAB ═══════════ */}
        {activeTab === 'recruiter' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="admin-recruiter-tab">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h1 className={`text-2xl font-['Cabinet_Grotesk'] font-bold mb-1 ${darkMode ? 'text-white' : 'text-slate-900'}`}>Agent Recruiter</h1>
                <p className="text-sm text-slate-400">LinkedIn outreach, email campaigns, social posts, and conversion analytics.</p>
              </div>
            </div>

            {/* Sub-tabs */}
            <div className="flex gap-1 mb-6 border-b border-slate-200 pb-2">
              {[
                { id: 'outreach', label: 'Outreach & Prospects' },
                { id: 'analytics', label: 'Conversion Analytics' },
                { id: 'social', label: 'Social Posts' },
                { id: 'smart-match', label: 'Smart Match' },
              ].map(st => (
                <button key={st.id} onClick={() => setRecruiterSubTab(st.id)}
                  className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${recruiterSubTab === st.id ? 'bg-[#002FA7] text-white' : 'text-slate-500 hover:bg-slate-100'}`}
                  data-testid={`recruiter-subtab-${st.id}`}>
                  {st.label}
                </button>
              ))}
            </div>

            {/* Stats (always visible) */}
            {recruiterStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <StatCard label="Total Prospects" value={recruiterStats.total} icon={Users} />
                <StatCard label="Contacted" value={recruiterStats.by_status?.contacted || 0} icon={Linkedin} sub={`${recruiterStats.by_status?.replied || 0} replied`} />
                <StatCard label="Interviewed" value={recruiterStats.by_status?.interviewed || 0} icon={CheckCircle2} sub={`${recruiterStats.by_status?.applied || 0} applied`} />
                <StatCard label="Hired" value={recruiterStats.by_status?.hired || 0} icon={Trophy} />
              </div>
            )}

            {/* ===== OUTREACH SUB-TAB ===== */}
            {recruiterSubTab === 'outreach' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left: Templates + Search Links */}
              <div className="lg:col-span-1 space-y-4">
                {/* LinkedIn Search Links */}
                <div className="bg-white border border-slate-200 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2"><Search size={14} className="text-[#002FA7]" /> Quick Search</h3>
                  <div className="space-y-1.5">
                    {searchLinks.map(link => (
                      <a key={link.id} href={link.url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center justify-between p-2.5 rounded-lg border border-slate-100 hover:border-[#0A66C2]/30 hover:bg-blue-50/30 transition-all group text-xs" data-testid={`search-${link.id}`}>
                        <div>
                          <div className="font-medium text-slate-700 group-hover:text-[#0A66C2]">{link.label}</div>
                          <div className="text-[10px] text-slate-400">{link.market}</div>
                        </div>
                        <ExternalLink size={12} className="text-slate-300 group-hover:text-[#0A66C2]" />
                      </a>
                    ))}
                  </div>
                </div>

                {/* Message Templates */}
                <div className="bg-white border border-slate-200 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2"><PenSquare size={14} className="text-[#002FA7]" /> Message Templates</h3>
                  <div className="space-y-1.5">
                    {recruiterTemplates.map(tmpl => (
                      <button key={tmpl.id} onClick={() => setActiveTemplate(activeTemplate === tmpl.id ? null : tmpl.id)}
                        className={`w-full text-left p-2.5 rounded-lg border transition-all text-xs ${activeTemplate === tmpl.id ? 'border-[#002FA7] bg-[#EFF3FF]' : 'border-slate-100 hover:border-slate-300'}`}
                        data-testid={`template-${tmpl.id}`}>
                        <div className="font-medium text-slate-700">{tmpl.name}</div>
                        <div className="text-[10px] text-slate-400 capitalize">{tmpl.category}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Center: Template Preview + Personalization */}
              <div className="lg:col-span-2 space-y-4">
                {activeTemplate ? (() => {
                  const tmpl = recruiterTemplates.find(t => t.id === activeTemplate);
                  if (!tmpl) return null;
                  const personalized = tmpl.body
                    .replace(/\{name\}/g, templateFields.name || '[Agent Name]')
                    .replace(/\{market\}/g, templateFields.market || '[Market]')
                    .replace(/\{state\}/g, templateFields.state || '[State]')
                    .replace(/\{specialization\}/g, templateFields.specialization || '[Specialization]');
                  const personalizedSubject = (tmpl.subject || '')
                    .replace(/\{market\}/g, templateFields.market || '[Market]');
                  return (
                    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="template-preview">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-semibold text-slate-800">{tmpl.name}</h3>
                        <div className="flex gap-2">
                          <Button onClick={() => { navigator.clipboard.writeText(personalized); toast.success('Message copied!'); }}
                            className="bg-[#0A66C2] hover:bg-[#004182] text-white text-[10px] h-7 rounded-lg gap-1" data-testid="copy-message-btn">
                            <Copy size={10} /> Copy
                          </Button>
                        </div>
                      </div>

                      {/* Send via Email */}
                      <div className="flex items-center gap-2 mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg" data-testid="send-email-section">
                        <Mail size={14} className="text-[#002FA7] shrink-0" />
                        <Input value={sendEmail} onChange={e => setSendEmail(e.target.value)} placeholder="agent@email.com" className="h-7 text-xs flex-1" type="email" data-testid="recruiter-email-input" />
                        <Button onClick={async () => {
                          if (!sendEmail.trim()) { toast.error('Enter an email address'); return; }
                          setSendingEmail(true);
                          try {
                            await axios.post(`${API}/admin/recruiter/send-email`, { to_email: sendEmail.trim(), subject: personalizedSubject || `Opportunity at Krieger Real Estate`, body: personalized, template_id: tmpl.id }, { withCredentials: true });
                            toast.success(`Email sent to ${sendEmail}`);
                            setSendEmail('');
                          } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send'); }
                          setSendingEmail(false);
                        }} disabled={sendingEmail} className="bg-[#002FA7] hover:bg-[#002075] text-white text-[10px] h-7 rounded-lg gap-1 px-3" data-testid="send-email-btn">
                          {sendingEmail ? <Loader2 size={10} className="animate-spin" /> : <Mail size={10} />} Send Email
                        </Button>
                      </div>
                      {/* Personalization fields */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4 p-3 bg-slate-50 rounded-lg">
                        <div>
                          <Label className="text-[10px] text-slate-400">Agent Name</Label>
                          <Input value={templateFields.name} onChange={e => setTemplateFields(f => ({...f, name: e.target.value}))} placeholder="John Smith" className="h-7 text-xs" data-testid="field-name" />
                        </div>
                        <div>
                          <Label className="text-[10px] text-slate-400">Market</Label>
                          <Select value={templateFields.market} onValueChange={v => {
                            const stateMap = { 'New York': 'NY', 'Miami': 'FL', 'Philadelphia': 'PA' };
                            setTemplateFields(f => ({...f, market: v, state: stateMap[v] || v}));
                          }}>
                            <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="New York">New York</SelectItem><SelectItem value="Miami">Miami</SelectItem><SelectItem value="Philadelphia">Philadelphia</SelectItem></SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[10px] text-slate-400">State</Label>
                          <Input value={templateFields.state} onChange={e => setTemplateFields(f => ({...f, state: e.target.value}))} className="h-7 text-xs" />
                        </div>
                        <div>
                          <Label className="text-[10px] text-slate-400">Specialization</Label>
                          <Input value={templateFields.specialization} onChange={e => setTemplateFields(f => ({...f, specialization: e.target.value}))} className="h-7 text-xs" />
                        </div>
                      </div>
                      {personalizedSubject && (
                        <div className="mb-2"><Label className="text-[10px] text-slate-400">Subject</Label><div className="text-sm font-medium text-slate-800 bg-slate-50 p-2 rounded">{personalizedSubject}</div></div>
                      )}
                      <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-700 whitespace-pre-line leading-relaxed font-mono" style={{ fontSize: '12px' }}>
                        {personalized}
                      </div>
                    </div>
                  );
                })() : (
                  <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
                    <Linkedin size={40} className="text-slate-200 mx-auto mb-4" />
                    <p className="text-slate-400 text-sm">Select a message template to preview and personalize.</p>
                  </div>
                )}

                {/* Prospect Tracker */}
                <div className="bg-white border border-slate-200 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Users size={14} className="text-[#002FA7]" /> Prospect Tracker</h3>
                    <Button onClick={() => setShowProspectForm(!showProspectForm)} className="bg-[#002FA7] hover:bg-[#002075] text-white text-[10px] h-7 rounded-lg gap-1" data-testid="add-prospect-btn">
                      <Plus size={12} /> Add Prospect
                    </Button>
                  </div>

                  {showProspectForm && (
                    <div className="bg-slate-50 rounded-lg p-3 mb-4" data-testid="prospect-form">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
                        <div><Label className="text-[10px] text-slate-400">Name *</Label><Input value={prospectForm.name} onChange={e => setProspectForm(f => ({...f, name: e.target.value}))} className="h-7 text-xs" data-testid="prospect-name" /></div>
                        <div><Label className="text-[10px] text-slate-400">LinkedIn URL</Label><Input value={prospectForm.linkedin_url} onChange={e => setProspectForm(f => ({...f, linkedin_url: e.target.value}))} className="h-7 text-xs" placeholder="https://linkedin.com/in/..." /></div>
                        <div><Label className="text-[10px] text-slate-400">Market</Label><Select value={prospectForm.market} onValueChange={v => setProspectForm(f => ({...f, market: v}))}><SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="New York">New York</SelectItem><SelectItem value="Miami">Miami</SelectItem><SelectItem value="Philadelphia">Philadelphia</SelectItem></SelectContent></Select></div>
                        <div><Label className="text-[10px] text-slate-400">Specialization</Label><Input value={prospectForm.specialization} onChange={e => setProspectForm(f => ({...f, specialization: e.target.value}))} className="h-7 text-xs" placeholder="Residential, Commercial..." /></div>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={async () => {
                          if (!prospectForm.name) { toast.error('Name required'); return; }
                          try { await axios.post(`${API}/admin/recruiter/prospects`, prospectForm, { withCredentials: true }); toast.success('Prospect added'); setShowProspectForm(false); setProspectForm({ name: '', linkedin_url: '', market: 'New York', specialization: '', notes: '' }); fetchRecruiter(); } catch { toast.error('Failed'); }
                        }} className="bg-[#002FA7] text-white text-[10px] h-7 rounded-lg" data-testid="save-prospect-btn">Add</Button>
                        <Button variant="ghost" onClick={() => setShowProspectForm(false)} className="text-[10px] h-7">Cancel</Button>
                      </div>
                    </div>
                  )}

                  {/* Filter */}
                  <div className="flex gap-1 mb-3 flex-wrap">
                    {['all', 'identified', 'contacted', 'replied', 'interviewed', 'applied', 'hired'].map(s => (
                      <button key={s} onClick={() => setProspectFilter(s)}
                        className={`px-2.5 py-1 rounded-full text-[10px] font-medium capitalize ${prospectFilter === s ? 'bg-[#002FA7] text-white' : 'bg-slate-100 text-slate-500'}`}>
                        {s}{recruiterStats?.by_status?.[s] ? ` (${recruiterStats.by_status[s]})` : s === 'all' ? ` (${prospects.length})` : ''}
                      </button>
                    ))}
                  </div>

                  {prospects.filter(p => prospectFilter === 'all' || p.status === prospectFilter).length === 0 ? (
                    <p className="text-center text-sm text-slate-400 py-6">No prospects yet. Use the search links to find agents on LinkedIn, then track them here.</p>
                  ) : (
                    <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
                      {prospects.filter(p => prospectFilter === 'all' || p.status === prospectFilter).map(p => {
                        const statusColors = { identified: 'bg-slate-100 text-slate-600', contacted: 'bg-blue-50 text-blue-700', replied: 'bg-amber-50 text-amber-700', interviewed: 'bg-violet-50 text-violet-700', applied: 'bg-emerald-50 text-emerald-700', hired: 'bg-emerald-100 text-emerald-800', passed: 'bg-red-50 text-red-600' };
                        return (
                          <div key={p.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-100 hover:bg-slate-50/50" data-testid={`prospect-${p.id}`}>
                            <div className="w-7 h-7 rounded-full bg-[#0A66C2]/10 flex items-center justify-center text-[10px] font-bold text-[#0A66C2] shrink-0">{p.name.charAt(0)}</div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-slate-800 truncate">{p.name}</span>
                                {p.linkedin_url && <a href={p.linkedin_url} target="_blank" rel="noreferrer" className="text-[#0A66C2]"><ExternalLink size={10} /></a>}
                              </div>
                              <div className="text-[10px] text-slate-400">{p.market}{p.specialization ? ` · ${p.specialization}` : ''}</div>
                            </div>
                            <Select value={p.status} onValueChange={async v => {
                              try { await axios.patch(`${API}/admin/recruiter/prospects/${p.id}`, { status: v }, { withCredentials: true }); fetchRecruiter(); } catch {}
                            }}>
                              <SelectTrigger className={`h-6 w-24 text-[9px] border-0 ${statusColors[p.status] || ''}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {['identified', 'contacted', 'replied', 'interviewed', 'applied', 'hired', 'passed'].map(s => (
                                  <SelectItem key={s} value={s} className="text-xs capitalize">{s}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={async () => {
                              try { await axios.delete(`${API}/admin/recruiter/prospects/${p.id}`, { withCredentials: true }); fetchRecruiter(); } catch {}
                            }}><Trash2 size={10} /></Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
            )}

            {/* ===== ANALYTICS SUB-TAB ===== */}
            {recruiterSubTab === 'analytics' && recruiterAnalytics && (
              <div className="space-y-6" data-testid="recruiter-analytics">
                {/* Overall Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-white border border-slate-200 p-4 rounded-xl"><div className="text-[10px] tracking-wider uppercase text-slate-400 font-semibold">Response Rate</div><div className="text-3xl font-['Cabinet_Grotesk'] font-bold text-[#002FA7]">{recruiterAnalytics.overall_response_rate}%</div><div className="text-xs text-slate-400">of contacted prospects replied</div></div>
                  <div className="bg-white border border-slate-200 p-4 rounded-xl"><div className="text-[10px] tracking-wider uppercase text-slate-400 font-semibold">Conversion Rate</div><div className="text-3xl font-['Cabinet_Grotesk'] font-bold text-emerald-600">{recruiterAnalytics.overall_conversion_rate}%</div><div className="text-xs text-slate-400">prospects who applied</div></div>
                  <div className="bg-white border border-slate-200 p-4 rounded-xl"><div className="text-[10px] tracking-wider uppercase text-slate-400 font-semibold">Hire Rate</div><div className="text-3xl font-['Cabinet_Grotesk'] font-bold text-amber-600">{recruiterAnalytics.overall_hire_rate}%</div><div className="text-xs text-slate-400">prospects hired</div></div>
                  <div className="bg-white border border-slate-200 p-4 rounded-xl"><div className="text-[10px] tracking-wider uppercase text-slate-400 font-semibold">Emails Sent</div><div className="text-3xl font-['Cabinet_Grotesk'] font-bold text-slate-800">{recruiterAnalytics.total_emails_sent}</div><div className="text-xs text-slate-400">recruiter outreach emails</div></div>
                </div>

                {/* Template Performance */}
                <div className="bg-white border border-slate-200 rounded-xl p-6">
                  <h3 className="text-sm font-semibold text-slate-800 mb-4">Template Performance</h3>
                  {recruiterAnalytics.template_performance.filter(t => t.total_sent > 0).length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-6">No template data yet. Start tracking prospects with templates to see performance metrics.</p>
                  ) : (
                    <div className="space-y-3">
                      {recruiterAnalytics.template_performance.map(t => (
                        <div key={t.template_id} className="flex items-center gap-4 p-3 rounded-lg bg-slate-50">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-slate-800">{t.template_name}</div>
                            <div className="text-xs text-slate-400">{t.total_sent} sent &middot; {t.emails_sent} via email</div>
                          </div>
                          <div className="flex gap-6 text-center shrink-0">
                            <div><div className="text-lg font-bold text-[#002FA7]">{t.response_rate}%</div><div className="text-[9px] text-slate-400">Response</div></div>
                            <div><div className="text-lg font-bold text-emerald-600">{t.conversion_rate}%</div><div className="text-[9px] text-slate-400">Conversion</div></div>
                            <div><div className="text-lg font-bold text-slate-600">{t.replied}/{t.hired}</div><div className="text-[9px] text-slate-400">Reply/Hire</div></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Recruitment Funnel */}
                <div className="bg-white border border-slate-200 rounded-xl p-6">
                  <h3 className="text-sm font-semibold text-slate-800 mb-4">Recruitment Pipeline</h3>
                  <div className="space-y-2">
                    {[
                      { key: 'identified', label: 'Identified', color: 'bg-slate-400' },
                      { key: 'contacted', label: 'Contacted', color: 'bg-blue-500' },
                      { key: 'replied', label: 'Replied', color: 'bg-amber-500' },
                      { key: 'interviewed', label: 'Interviewed', color: 'bg-violet-500' },
                      { key: 'applied', label: 'Applied', color: 'bg-emerald-500' },
                      { key: 'hired', label: 'Hired', color: 'bg-[#002FA7]' },
                    ].map(step => {
                      const count = recruiterAnalytics.funnel[step.key] || 0;
                      const pct = recruiterAnalytics.funnel_total > 0 ? Math.round((count / recruiterAnalytics.funnel_total) * 100) : 0;
                      return (
                        <div key={step.key} className="flex items-center gap-3">
                          <span className="text-xs text-slate-500 w-24 text-right">{step.label}</span>
                          <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full ${step.color} rounded-full transition-all duration-500 flex items-center justify-end pr-2`} style={{ width: `${Math.max(pct, 3)}%` }}>
                              {pct > 8 && <span className="text-[9px] font-bold text-white">{count}</span>}
                            </div>
                          </div>
                          {pct <= 8 && <span className="text-xs font-semibold text-slate-600 w-6">{count}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Market Breakdown */}
                {recruiterAnalytics.market_breakdown.length > 0 && (
                  <div className="bg-white border border-slate-200 rounded-xl p-6">
                    <h3 className="text-sm font-semibold text-slate-800 mb-4">Performance by Market</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {recruiterAnalytics.market_breakdown.map(m => (
                        <div key={m.market} className="bg-slate-50 rounded-lg p-4">
                          <div className="text-sm font-semibold text-slate-800 mb-2">{m.market}</div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div><span className="text-slate-400">Prospects</span><div className="font-bold text-slate-700">{m.total}</div></div>
                            <div><span className="text-slate-400">Contacted</span><div className="font-bold text-slate-700">{m.contacted}</div></div>
                            <div><span className="text-slate-400">Responded</span><div className="font-bold text-emerald-600">{m.replied}</div></div>
                            <div><span className="text-slate-400">Response Rate</span><div className="font-bold text-[#002FA7]">{m.response_rate}%</div></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ===== SOCIAL POSTS SUB-TAB ===== */}
            {recruiterSubTab === 'social' && (
              <div className="space-y-6" data-testid="recruiter-social">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Reddit Posts */}
                  <div className="bg-white border border-slate-200 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 rounded-lg bg-[#FF4500]/10 flex items-center justify-center"><span className="text-sm font-bold text-[#FF4500]">r/</span></div>
                      <div><h3 className="text-sm font-semibold text-slate-800">Reddit Posts</h3><p className="text-[10px] text-slate-400">Copy and post to relevant subreddits</p></div>
                    </div>
                    <div className="space-y-3">
                      {[
                        { sub: 'r/realestate', title: 'Cloud-based brokerage offering up to 90% commission — looking for agents in NY, PA, FL', body: `Hi everyone,\n\nI'm the broker of record at Krieger Real Estate, a cloud-based brokerage offering up to 90% commission splits with zero monthly fees.\n\nWe're looking for licensed residential and commercial agents in New York, Pennsylvania, and Florida. No desk fees, no technology charges — just a simple split that lets you keep more of what you earn.\n\nFull cloud-based platform with CRM, deal tracking, document management, and AI tools included.\n\nLearn more at joinkrieger.com or DM me with questions.\n\n— Joel N. Krieger, Broker of Record` },
                        { sub: 'r/commercialrealestate', title: 'Up to 90% commission for commercial agents — multifamily, office, retail, industrial', body: `Krieger Real Estate is actively recruiting commercial agents across NY, PA, and FL.\n\nUp to 90/10 split, zero fees. Multifamily, office, retail, industrial, and leasing.\n\nCloud-based platform with full deal pipeline tracking, CRM, and document management.\n\nApply: joinkrieger.com/apply` },
                        { sub: 'r/realtors', title: 'Tired of 70/30 splits and desk fees? Looking for agents in NY, PA, and FL', body: `If you're a licensed agent looking for a better commission structure, Krieger Real Estate offers up to 90% commission with:\n\n• Zero monthly fees\n• Zero desk fees\n• Full cloud-based platform\n• Statewide coverage in NY, PA, and FL\n\nWe recruit both residential and commercial agents.\n\nDetails: joinkrieger.com` },
                      ].map((post, i) => (
                        <div key={i} className="border border-slate-100 rounded-lg p-4 hover:border-[#FF4500]/30 transition-colors">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] font-semibold text-[#FF4500]">{post.sub}</span>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" className="h-6 text-[9px] text-slate-500" onClick={() => { navigator.clipboard.writeText(post.title); toast.success('Title copied'); }}>Copy Title</Button>
                              <Button variant="ghost" size="sm" className="h-6 text-[9px] text-slate-500" onClick={() => { navigator.clipboard.writeText(post.body); toast.success('Body copied'); }}>Copy Body</Button>
                            </div>
                          </div>
                          <div className="text-xs font-medium text-slate-800 mb-1">{post.title}</div>
                          <div className="text-[11px] text-slate-500 whitespace-pre-line leading-relaxed max-h-24 overflow-hidden">{post.body}</div>
                          <a href={`https://www.reddit.com/${post.sub}/submit`} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 mt-2 text-[10px] font-semibold text-[#FF4500] hover:underline" data-testid={`reddit-post-${i}`}>
                            Post to {post.sub} <ExternalLink size={10} />
                          </a>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <p className="text-[10px] text-slate-400">Tip: Search for local subreddits like r/nycrealEstate, r/miami, r/philadelphia for targeted posts.</p>
                    </div>
                  </div>

                  {/* X / Twitter Posts */}
                  <div className="bg-white border border-slate-200 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 rounded-lg bg-black/5 flex items-center justify-center"><span className="text-sm font-bold text-black">𝕏</span></div>
                      <div><h3 className="text-sm font-semibold text-slate-800">X (Twitter) Posts</h3><p className="text-[10px] text-slate-400">Copy and post to your X account</p></div>
                    </div>
                    <div className="space-y-3">
                      {[
                        { text: `Real estate agents: are you keeping 90% of your commission?\n\nIf not, let's talk.\n\nKrieger Real Estate — up to 90/10 split, zero monthly fees, cloud-based platform.\n\nNow recruiting in NY, PA, and FL.\n\njoinkrieger.com`, hashtags: '#RealEstate #RealtorLife #CommissionSplit #NYRealEstate #MiamiRealEstate' },
                        { text: `Thinking about switching brokerages?\n\nHere's what to look for:\n✅ Up to 90% commission\n✅ Zero desk fees\n✅ Zero monthly fees\n✅ Modern technology (CRM, AI tools)\n✅ Statewide coverage\n\nThat's exactly what we offer.\n\njoinkrieger.com/apply`, hashtags: '#RealEstateAgent #BrokerageSwitch #90Percent' },
                        { text: `Commercial real estate agents in NYC, Miami, and Philly:\n\nWe're hiring.\n\nUp to 90% commission. Multifamily, office, retail, industrial.\n\nNo desk fees. No monthly fees. All the tools.\n\nApply: joinkrieger.com`, hashtags: '#CRE #CommercialRealEstate #NYCRealEstate #MiamiRE #PhillyRealEstate' },
                        { text: `New agents: don't start your career at a 60/40 split.\n\nKrieger Real Estate offers up to 90% commission for qualified agents — even newer ones.\n\nFull cloud-based platform, CRM, deal tracking, AI tools.\n\nNY • PA • FL\n\njoinkrieger.com`, hashtags: '#NewAgent #RealEstateCareer #JoinKrieger' },
                        { text: `The math is simple:\n\n$1M deal at 3% commission:\n• 70/30 brokerage: you keep $21,000\n• Krieger (up to 90/10): you keep $27,000\n\nThat's $6,000 more. Per deal.\n\nMultiply that by your annual volume.\n\njoinkrieger.com/agent-income-calculator`, hashtags: '#RealEstateIncome #CommissionCalculator #RealtorMath' },
                      ].map((tweet, i) => (
                        <div key={i} className="border border-slate-100 rounded-lg p-4 hover:border-black/10 transition-colors">
                          <div className="text-[11px] text-slate-700 whitespace-pre-line leading-relaxed mb-2">{tweet.text}</div>
                          <div className="text-[10px] text-blue-500 mb-2">{tweet.hashtags}</div>
                          <div className="flex gap-2">
                            <Button variant="ghost" size="sm" className="h-6 text-[9px] text-slate-500" onClick={() => { navigator.clipboard.writeText(tweet.text + '\n\n' + tweet.hashtags); toast.success('Tweet copied'); }} data-testid={`copy-tweet-${i}`}>Copy</Button>
                            <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(tweet.text + '\n\n' + tweet.hashtags)}`} target="_blank" rel="noopener noreferrer">
                              <Button variant="ghost" size="sm" className="h-6 text-[9px] text-black gap-1" data-testid={`post-tweet-${i}`}>Post to 𝕏 <ExternalLink size={8} /></Button>
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ===== SMART MATCH SUB-TAB ===== */}
            {recruiterSubTab === 'smart-match' && (
              <SmartMatchPanel api={API} />
            )}
          </motion.div>
        )}
        {activeTab === 'reports' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="admin-reports-tab">
            <h1 className="text-2xl font-['Cabinet_Grotesk'] font-bold text-slate-900 mb-2">Reports & Exports</h1>
            <p className="text-sm text-slate-400 mb-6">Download CSV reports for your brokerage data.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { key: 'applications', title: 'All Applications', desc: 'Agent name, contact, license, status, ICA, split, deal source, referral', icon: Users },
                { key: 'teams', title: 'Team Roster', desc: 'Teams with leader, members, roles, and individual commission splits', icon: Building2 },
                { key: 'ica-status', title: 'ICA Signing Status', desc: 'All agents with ICA signed/unsigned status and signing dates', icon: FileSignature },
                { key: 'deal-sources', title: 'Deal Source Breakdown', desc: 'Source counts and percentages for all applications', icon: PieChart },
                { key: 'referrals', title: 'Referral Report', desc: 'All referred applicants with referrer info and status', icon: Share2 },
              ].map(report => (
                <div key={report.key} className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col justify-between" data-testid={`report-${report.key}`}>
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-9 h-9 rounded-lg bg-[#EFF3FF] flex items-center justify-center">
                        <report.icon size={16} className="text-[#002FA7]" />
                      </div>
                      <h3 className="text-sm font-semibold text-slate-800 font-['Cabinet_Grotesk']">{report.title}</h3>
                    </div>
                    <p className="text-xs text-slate-400 mb-4 leading-relaxed">{report.desc}</p>
                  </div>
                  <a
                    href={`${API}/admin/export/${report.key}`}
                    className="inline-flex items-center justify-center gap-1.5 bg-[#002FA7] text-white hover:bg-[#002075] rounded-lg px-4 py-2 text-xs font-medium transition-colors"
                    data-testid={`export-${report.key}-btn`}
                  >
                    <Download size={12} /> Export CSV
                  </a>
                </div>
              ))}
            </div>
          </motion.div>
        )}

      </div>
    </div>
  );
}
